/**
 * Curtain Customizer Form Wizard
 * Handles step navigation, form validation, price calculation, and cart integration
 */

class CurtainCustomizer {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.currentStep = 1;
    this.totalSteps = 4;
    this.isAddingToCart = false;
    this.isAddingCushionFromSuccess = false;
    this.isStep4Completed = false;
    this.maxWidthMeters = 8;
    this.maxDropMeters = 2.7;
    this.lastDisplayedPriceText = '';
    this.headingNoticeType = '';
    this.cushionUpsell = null;
    this.cushionsAddedWithCurtain = false;
    this.waveFullnessNote = '';
    this.waveFullnessConfirm = '';
    this.waterfallForcedNote = '';
    this.waveHooksLabel = '';
    this.waveHooksDescription = '';
    this.waveHooksMismatchWarning = '';
    this.waveHooksConfirmLabel = '';
    this.waveHooksCalculatedLabel = '';
    this.waveHooksRangeError = '';
    this.waveWidthMismatchTolerance = 0;
    this.reviewPreviewImages = {
      single: '',
      pair: '',
      waterfall: ''
    };
    this.reviewOverlayPositions = {};
    this.formData = {
      headingStyle: null,
      width: null,
      drop: null,
      panelType: null,
      fullness: null,
      joinType: null,
      roomName: null,
      addScatterCushions: false,
      trackHookCount: null,
      waveHooksMismatchAcknowledged: false
    };
    
    this.init();
  }

  init() {
    this.container = document.querySelector(`[data-section-id="${this.sectionId}"]`);
    if (!this.container) return;

    this.form = this.container.querySelector(`#curtain-customizer-form-${this.sectionId}`);
    if (!this.form) return;
    this.waveFullnessNote = '';
    this.waveFullnessConfirm = '';
    this.waterfallForcedNote = this.form.dataset.waterfallForcedNote || 'Waterfall heading uses fixed panel settings: Panel Type is set to Pair and Middle of Curtain is set to Joined.';
    this.waveHooksLabel = this.form.dataset.waveHooksLabel || 'Track hooks';
    this.waveHooksDescription = this.form.dataset.waveHooksDescription || '';
    this.waveHooksMismatchWarning = this.form.dataset.waveHooksMismatchWarning || 'The width from your hook count does not match your track width and fullness. Please double-check your hook count, or contact us if your measurements are correct.';
    this.waveHooksConfirmLabel = this.form.dataset.waveHooksConfirmLabel || 'I have double-checked my hook count';
    this.waveHooksCalculatedLabel = this.form.dataset.waveHooksCalculatedLabel || 'Fabric width from hooks';
    this.waveHooksRangeError = this.form.dataset.waveHooksRangeError || 'Hook count must give a fabric width between 1.00 m and 20.00 m.';
    const mismatchTolerancePct = parseFloat(this.form.dataset.waveHooksMismatchTolerancePercent || 0);
    this.waveWidthMismatchTolerance = Number.isFinite(mismatchTolerancePct) && mismatchTolerancePct >= 0
      ? mismatchTolerancePct / 100
      : 0;
    this.reviewPreviewImages.single = this.form.dataset.reviewPreviewSingleImage || '';
    this.reviewPreviewImages.pair = this.form.dataset.reviewPreviewPairImage || '';
    this.reviewPreviewImages.waterfall = this.form.dataset.reviewPreviewWaterfallImage || '';
    this.reviewOverlayPositions = {
      single: {
        drop: { x: this.form.dataset.overlaySingleDropX, y: this.form.dataset.overlaySingleDropY },
        topLeft: { x: this.form.dataset.overlaySingleTopLeftX, y: this.form.dataset.overlaySingleTopLeftY },
        topRight: { x: this.form.dataset.overlaySingleTopRightX, y: this.form.dataset.overlaySingleTopRightY }
      },
      pair: {
        drop: { x: this.form.dataset.overlayPairDropX, y: this.form.dataset.overlayPairDropY },
        topLeft: { x: this.form.dataset.overlayPairTopLeftX, y: this.form.dataset.overlayPairTopLeftY },
        topRight: { x: this.form.dataset.overlayPairTopRightX, y: this.form.dataset.overlayPairTopRightY }
      },
      waterfall: {
        drop: { x: this.form.dataset.overlayWaterfallDropX, y: this.form.dataset.overlayWaterfallDropY },
        topLeft: { x: this.form.dataset.overlayWaterfallTopLeftX, y: this.form.dataset.overlayWaterfallTopLeftY },
        topRight: { x: this.form.dataset.overlayWaterfallTopRightX, y: this.form.dataset.overlayWaterfallTopRightY }
      }
    };
    
    // Check if already initialized to prevent duplicate event listeners
    if (this.form.dataset.initialized === 'true') {
      console.warn(`Curtain customizer for section ${this.sectionId} already initialized`);
      return;
    }
    this.form.dataset.initialized = 'true';

    console.info(
      `Curtain Customizer: init (section ${this.sectionId}, handle: ${this.form.dataset.productHandle || 'pending'})`
    );
    
    this.steps = this.container.querySelectorAll('.curtain-customizer__step');
    this.stepIndicators = this.container.querySelectorAll('.step-indicator__item');
    
    // Sort heading/fullness options by desired display order.
    this.sortHeadingOptions();
    this.sortFullnessOptions();
    
    // Set default selections
    this.setDefaultFullness();
    this.setDefaultHeadingStyle();
    
    // Get variant data
    const variantIdInput = this.form.querySelector('input[name="variant_id"]');
    const variantPriceInput = this.form.querySelector('input[name="variant_price"]');
    const shopCurrencyInput = this.form.querySelector('input[name="shop_currency"]');
    this.variantId = variantIdInput ? (variantIdInput.value || variantIdInput.dataset.variantId || null) : null;
    this.variantPrice = variantPriceInput ? parseFloat(variantPriceInput.value || variantPriceInput.dataset.variantPrice || 0) : 0;
    this.shopCurrency = shopCurrencyInput ? shopCurrencyInput.value : 'USD';
    
    // Load variants mapping for width (+ optional heading style) variant selection
    this.hasHeadingVariants = this.form.dataset.hasHeadingVariants === 'true';
    this.variantsMap = {};
    this.variantsData = {};
    this.availableWidthsByHeading = {};
    this.variantsLoadPromise = null;
    this.variantsLoaded = false;
    this.variantsFetchSettled = false;
    this.productHeadingKeys = new Set();
    this.shopifyHeadingOptions = [];
    const headingOptionsScript = this.container.querySelector(`#heading-style-options-${this.sectionId}`);
    if (headingOptionsScript) {
      try {
        const options = JSON.parse(headingOptionsScript.textContent);
        if (Array.isArray(options)) {
          this.shopifyHeadingOptions = options.filter(Boolean);
        }
      } catch (e) {
        console.error('Error parsing heading style options:', e);
      }
    }

    this.productHandle = this.form.dataset.productHandle || '';
    this.storefrontAccessToken = (this.form.dataset.storefrontAccessToken || '').trim();
    this.widthOptionIndex = parseInt(this.form.dataset.widthOptionIndex, 10) || 0;
    this.headingOptionIndex = parseInt(this.form.dataset.headingOptionIndex, 10) || 0;
    this.expectedVariantsCount = parseInt(this.form.dataset.variantsCount, 10) || 0;
    this.resolveProductHandle();

    const debugScript = this.container.querySelector(`#product-debug-${this.sectionId}`);
    if (debugScript) {
      try {
        const debugData = JSON.parse(debugScript.textContent);
        if (!this.productHandle && debugData.selected_product_handle) {
          this.productHandle = debugData.selected_product_handle;
        }
        if (debugData.variants_count && debugData.variants_count > this.expectedVariantsCount) {
          this.expectedVariantsCount = debugData.variants_count;
        }
        if (!this.variantId && debugData.has_variant === false) {
          console.warn('No variant found, but product has variants:', debugData.variants_count);
        }
      } catch (e) {
        console.error('Error parsing debug data:', e);
      }
    }

    if (!this.productHandle && typeof window.Shopify !== 'undefined') {
      const metaProduct = window.Shopify?.analytics?.meta?.product;
      if (metaProduct && metaProduct.handle) {
        this.productHandle = metaProduct.handle;
      }
    }

    if (!this.productHandle) {
      this.variantsFetchSettled = true;
    }

    const variantsMappingScript = this.container.querySelector(`#variants-mapping-${this.sectionId}`);
    if (variantsMappingScript) {
      try {
        const variantsArray = JSON.parse(variantsMappingScript.textContent);
        this.buildVariantsMapFromEntries(variantsArray, { fromInlineEmbed: true });
      } catch (e) {
        console.error('Error parsing variants mapping:', e);
      }
    }

    this.updateProductConfigErrorVisibility();

    const cushionDataScript = this.container.querySelector(`#cushion-upsell-data-${this.sectionId}`);
    if (cushionDataScript) {
      try {
        this.cushionUpsell = JSON.parse(cushionDataScript.textContent);
      } catch (e) {
        console.error('Error parsing cushion upsell data:', e);
        this.cushionUpsell = null;
      }
    }

    this.setupEventListeners();
    this.setupCushionUpsellUI();
    this.updateStepCountForEyelet();
    this.showStep(this.currentStep, { skipScroll: true });
    this.validateDimensionsStep();
    this.validateFullnessStep();
    this.validateHeadingStep();
    this.validateEyeletStep();
    this.validateReviewStep();
    this.updatePrice();
    this.updateReviewMeasurementPreview();
    this.updateStepIndicator();

    this.loadFullVariantsMapping().then(() => {
      this.updatePrice();
      this.updateSummaryWidthDisplay();
    });
  }

  resolveProductHandle() {
    if (this.productHandle) return this.productHandle;

    const metaScript = this.container.querySelector(`#cc-product-meta-${this.sectionId}`);
    if (metaScript) {
      try {
        const meta = JSON.parse(metaScript.textContent);
        if (meta && meta.handle) {
          this.productHandle = meta.handle;
          if (meta.variantsCount && meta.variantsCount > this.expectedVariantsCount) {
            this.expectedVariantsCount = meta.variantsCount;
          }
          if (meta.widthOptionIndex) this.widthOptionIndex = parseInt(meta.widthOptionIndex, 10);
          if (meta.headingOptionIndex) this.headingOptionIndex = parseInt(meta.headingOptionIndex, 10);
          return this.productHandle;
        }
      } catch (e) {
        console.error('Curtain Customizer: error parsing product meta:', e);
      }
    }

    const pathMatch = window.location.pathname.match(/\/products\/([^/?#]+)/i);
    if (pathMatch && pathMatch[1]) {
      this.productHandle = decodeURIComponent(pathMatch[1]);
      return this.productHandle;
    }

    if (typeof window.Shopify !== 'undefined') {
      const metaProduct = window.Shopify?.analytics?.meta?.product;
      if (metaProduct && metaProduct.handle) {
        this.productHandle = metaProduct.handle;
        return this.productHandle;
      }
    }

    return '';
  }

  getProductJsonUrls(handle) {
    const encoded = encodeURIComponent(handle);
    const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
    const urls = [
      `${root}/products/${encoded}.js`,
      `/products/${encoded}.js`
    ];
    return [...new Set(urls)];
  }

  normalizeCatalogEntries(rawEntries) {
    if (!Array.isArray(rawEntries)) return [];
    return rawEntries
      .map((entry) => {
        if (!entry || !entry.id) return null;
        const width = typeof entry.width === 'number'
          ? entry.width
          : this.parseWidthCmFromOption(entry.width);
        if (!width || width <= 0) return null;
        return {
          width,
          heading: entry.heading || null,
          id: String(entry.id),
          price: entry.price
        };
      })
      .filter(Boolean);
  }

  getWidthSkuCountFromProduct(product) {
    if (!product || !Array.isArray(product.options)) return 0;
    const widthOption = product.options.find((opt) => /^width$/i.test(String(opt.name || '').trim()));
    if (widthOption && Array.isArray(widthOption.values)) {
      return widthOption.values.length;
    }
    return 0;
  }

  getMetafieldCatalogEntries() {
    const script = this.container.querySelector(`#cc-metafield-catalog-${this.sectionId}`);
    if (!script) return [];
    try {
      const raw = JSON.parse(script.textContent);
      return this.normalizeCatalogEntries(Array.isArray(raw) ? raw : raw.variants || []);
    } catch (e) {
      console.error('Curtain Customizer: error parsing variant_price_catalog metafield', e);
      return [];
    }
  }

  getStorefrontApiUrl() {
    const shop = window.Shopify?.shop;
    if (!shop) return null;
    return `https://${shop}/api/2024-10/graphql.json`;
  }

  parseStorefrontVariantGid(gid) {
    if (!gid) return '';
    const match = String(gid).match(/ProductVariant\/(\d+)/);
    return match ? match[1] : '';
  }

  mapStorefrontVariantNode(node) {
    if (!node) return null;
    let width = 0;
    let heading = null;
    if (Array.isArray(node.selectedOptions)) {
      node.selectedOptions.forEach((opt) => {
        const name = String(opt.name || '').trim();
        if (/^width$/i.test(name)) {
          width = this.parseWidthCmFromOption(opt.value);
        } else if (/heading/i.test(name)) {
          heading = opt.value;
        }
      });
    }
    const id = this.parseStorefrontVariantGid(node.id);
    const amount = parseFloat(node.price?.amount);
    if (!id || !width || !Number.isFinite(amount)) return null;
    return {
      width,
      heading,
      id,
      price: Math.round(amount * 100)
    };
  }

  async fetchAllVariantsStorefront(handle) {
    const token = this.storefrontAccessToken;
    const apiUrl = this.getStorefrontApiUrl();
    if (!token || !apiUrl) return null;

    const query = `
      query ProductVariants($handle: String!, $cursor: String) {
        product(handle: $handle) {
          variants(first: 250, after: $cursor) {
            pageInfo { hasNextPage endCursor }
            edges {
              node {
                id
                price { amount }
                selectedOptions { name value }
              }
            }
          }
        }
      }
    `;

    let cursor = null;
    const allEntries = [];
    let pageCount = 0;
    const maxPages = 12;

    try {
      while (pageCount < maxPages) {
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token
          },
          body: JSON.stringify({
            query,
            variables: { handle, cursor }
          })
        });

        if (!response.ok) {
          console.warn('Curtain Customizer: Storefront API HTTP', response.status);
          break;
        }

        const payload = await response.json();
        if (payload.errors && payload.errors.length) {
          console.warn('Curtain Customizer: Storefront API', payload.errors[0].message);
          break;
        }

        const connection = payload.data?.product?.variants;
        if (!connection) break;

        connection.edges.forEach((edge) => {
          const entry = this.mapStorefrontVariantNode(edge.node);
          if (entry) allEntries.push(entry);
        });

        if (!connection.pageInfo?.hasNextPage) break;
        cursor = connection.pageInfo.endCursor;
        pageCount += 1;
      }
    } catch (error) {
      console.warn('Curtain Customizer: Storefront API fetch failed', error);
      return null;
    }

    if (allEntries.length > 0) {
      return { entries: allEntries, source: 'storefront-graphql' };
    }
    return null;
  }

  isCatalogLikelyIncomplete(entries, widthSkuCount) {
    if (!entries.length) return true;
    const maxWidth = entries.reduce((max, entry) => Math.max(max, entry.width || 0), 0);
    if (widthSkuCount > 84 && entries.length <= 250 && maxWidth < 1500) return true;
    if (this.expectedVariantsCount > 250 && entries.length <= 250 && maxWidth < 1900) return true;
    return false;
  }

  mapProductJsVariantsToEntries(product, widthIdx, headingIdx) {
    if (!product || !Array.isArray(product.variants)) return [];
    return product.variants
      .map((variant) => {
        const options = [variant.option1, variant.option2, variant.option3];
        const width = this.parseWidthCmFromOption(options[widthIdx - 1]);
        const heading = headingIdx ? options[headingIdx - 1] : null;
        return {
          width,
          heading: heading || null,
          id: String(variant.id),
          price: variant.price
        };
      })
      .filter((entry) => entry.width > 0 && entry.id);
  }

  parseWidthCmFromOption(value) {
    if (value == null || value === '') return 0;
    const cleaned = String(value).replace(/cm/gi, '').replace(/\s+/g, '').trim();
    const parsed = parseInt(cleaned, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }

  getHeadingKeyFromLabel(label) {
    if (!label) return '';
    const normalized = this.normalizeHeadingOptionValue(label);
    if (normalized) return normalized;
    return String(label).toLowerCase().replace(/\s+/g, ' ').trim();
  }

  buildVariantsMapFromEntries(entries, options = {}) {
    if (!Array.isArray(entries) || !entries.length) return;

    const nextMap = { ...this.variantsMap };
    const nextData = { ...this.variantsData };
    const nextHeadingKeys = new Set(this.productHeadingKeys);
    let hasHeadingInEntries = false;
    let maxWidthInBatch = 0;

    entries.forEach((variant) => {
      if (!variant || !variant.width || !variant.id) return;
      if (variant.price == null || variant.price === '') return;

      maxWidthInBatch = Math.max(maxWidthInBatch, variant.width);

      let headingKey = '';
      if (variant.heading) {
        hasHeadingInEntries = true;
        headingKey = this.getHeadingKeyFromLabel(variant.heading);
        if (headingKey && this.isKnownHeadingKey(headingKey)) {
          nextHeadingKeys.add(headingKey);
        }
      }

      const useHeadingKey = Boolean(variant.heading && (this.hasHeadingVariants || hasHeadingInEntries));
      const mapKey = useHeadingKey
        ? `${variant.width}|${headingKey}`
        : String(variant.width);

      nextMap[mapKey] = String(variant.id);
      const priceCents = typeof variant.price === 'number' ? variant.price : parseFloat(variant.price);
      nextData[mapKey] = {
        id: String(variant.id),
        price: priceCents / 100.0
      };
    });

    if (Object.keys(nextMap).length === 0) return;

    this.variantsMap = nextMap;
    this.variantsData = nextData;
    this.productHeadingKeys = nextHeadingKeys;
    if (hasHeadingInEntries) {
      this.hasHeadingVariants = true;
    }

    this.rebuildAvailableWidthsCache();

    const mapKeys = Object.keys(this.variantsMap).length;
    const maxWidthInMap = this.getMaxCatalogWidthCm();
    const entryCount = Math.max(entries.length, mapKeys);

    if (options.fromInlineEmbed) {
      return;
    }

    if (
      !this.productHandle ||
      entryCount >= this.expectedVariantsCount ||
      maxWidthInMap >= 2000 ||
      (this.expectedVariantsCount > 100 && maxWidthInBatch >= 1900)
    ) {
      this.variantsLoaded = true;
    }
  }

  getMaxCatalogWidthCm() {
    let max = 0;
    Object.keys(this.variantsMap).forEach((mapKey) => {
      const pipeIndex = mapKey.indexOf('|');
      const width = parseInt(pipeIndex >= 0 ? mapKey.slice(0, pipeIndex) : mapKey, 10);
      if (Number.isFinite(width) && width > max) max = width;
    });
    return max;
  }

  rebuildAvailableWidthsCache() {
    const byHeading = {};

    Object.keys(this.variantsMap).forEach((mapKey) => {
      const pipeIndex = mapKey.indexOf('|');
      const width = parseInt(pipeIndex >= 0 ? mapKey.slice(0, pipeIndex) : mapKey, 10);
      if (!Number.isFinite(width) || width <= 0) return;

      if (pipeIndex >= 0) {
        const headingKey = mapKey.slice(pipeIndex + 1);
        if (!byHeading[headingKey]) byHeading[headingKey] = new Set();
        byHeading[headingKey].add(width);
      } else {
        if (!byHeading._all) byHeading._all = new Set();
        byHeading._all.add(width);
      }
    });

    this.availableWidthsByHeading = {};
    Object.keys(byHeading).forEach((headingKey) => {
      this.availableWidthsByHeading[headingKey] = [...byHeading[headingKey]].sort((a, b) => a - b);
    });
  }

  getAvailableWidthsForHeading(headingKey) {
    if (!this.availableWidthsByHeading || !Object.keys(this.availableWidthsByHeading).length) {
      this.rebuildAvailableWidthsCache();
    }

    if (headingKey && this.availableWidthsByHeading[headingKey]) {
      return this.availableWidthsByHeading[headingKey];
    }

    if (this.availableWidthsByHeading._all && this.availableWidthsByHeading._all.length) {
      return this.availableWidthsByHeading._all;
    }

    const union = new Set();
    Object.values(this.availableWidthsByHeading).forEach((widths) => {
      widths.forEach((width) => union.add(width));
    });
    return [...union].sort((a, b) => a - b);
  }

  /**
   * Width SKUs are 100cm, 110cm, …, 2000cm. Calculated widths (hooks/fullness) may land
   * between SKUs (e.g. 922cm) — pick the smallest catalog width >= target (ceil to SKU).
   */
  snapToProductWidthGridCm(targetCm) {
    const clamped = Math.max(100, Math.min(2000, targetCm));
    if (clamped <= 100) return 100;
    const steps = Math.ceil((clamped - 100) / 10);
    return Math.min(2000, 100 + steps * 10);
  }

  resolveCatalogWidthCm(targetCm, headingKey) {
    const clamped = Math.max(100, Math.min(2000, targetCm));
    const widths = this.getAvailableWidthsForHeading(headingKey);

    if (widths.length) {
      const match = widths.find((width) => width >= clamped);
      if (match != null) return match;
    }

    return this.snapToProductWidthGridCm(clamped);
  }

  getVariantWidthForSelection(calculatedWidthCm, headingKeyOverride = undefined) {
    if (!Number.isFinite(calculatedWidthCm) || calculatedWidthCm <= 0) return 0;

    let headingKey = null;
    if (headingKeyOverride !== undefined) {
      headingKey = headingKeyOverride;
    } else if (this.hasHeadingVariants) {
      headingKey = this.getHeadingVariantKey();
    }

    let roundedCm;
    if (headingKey === 'eyelet') {
      roundedCm = this.roundWidthToNearest50(calculatedWidthCm);
    } else {
      roundedCm = this.roundWidthToNearest10(calculatedWidthCm);
    }
    if (roundedCm < 100) roundedCm = 100;
    if (roundedCm > 2000) return 0;

    if (headingKey === 'waterfall') return 0;

    return this.resolveCatalogWidthCm(roundedCm, headingKey) || 0;
  }

  lookupVariantMapKey(variantWidth, headingKey) {
    if (!variantWidth) return null;

    const candidates = [];
    if (headingKey) {
      candidates.push(`${variantWidth}|${headingKey}`);
    }
    if (this.formData.headingStyle) {
      const { name, handle, shopifyHeadingValue } = this.formData.headingStyle;
      [shopifyHeadingValue, name, handle].forEach((label) => {
        const key = this.getHeadingKeyFromLabel(label);
        if (key) candidates.push(`${variantWidth}|${key}`);
      });
    }
    this.shopifyHeadingOptions.forEach((optionValue) => {
      const key = this.getHeadingKeyFromLabel(optionValue);
      if (key) candidates.push(`${variantWidth}|${key}`);
    });
    candidates.push(String(variantWidth));

    for (const key of candidates) {
      if (this.variantsMap[key]) return key;
    }

    const prefix = `${variantWidth}|`;
    return Object.keys(this.variantsMap).find((key) => key.startsWith(prefix)) || null;
  }

  updateProductConfigErrorVisibility() {
    const productError = this.container.querySelector(`#product-config-error-${this.sectionId}`);
    if (!productError) return;

    if (Object.keys(this.variantsMap).length > 0 || this.variantId || this.variantPrice > 0) {
      productError.style.display = 'none';
    } else {
      productError.style.display = '';
      console.error('Curtain Customizer: No variants mapping or variant ID found');
    }
  }

  detectOptionIndicesFromProduct(product) {
    const optionNames = Array.isArray(product.options) ? product.options : [];
    let widthIdx = this.widthOptionIndex;
    let headingIdx = this.headingOptionIndex;

    if (!widthIdx) {
      const widthOptionIndex = optionNames.findIndex((name) => /^width$/i.test(String(name || '').trim()));
      if (widthOptionIndex >= 0) widthIdx = widthOptionIndex + 1;
    }

    if (!headingIdx) {
      const headingOptionIndex = optionNames.findIndex((name) => /heading/i.test(String(name || '').trim()));
      if (headingOptionIndex >= 0) headingIdx = headingOptionIndex + 1;
    }

    return { widthIdx, headingIdx };
  }

  async fetchProductJson(handle) {
    const urls = this.getProductJsonUrls(handle);
    let lastError = null;

    for (const url of urls) {
      try {
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) {
          lastError = new Error(`HTTP ${response.status} for ${url}`);
          continue;
        }
        const product = await response.json();
        if (product && Array.isArray(product.variants) && product.variants.length > 0) {
          return { product, url };
        }
        lastError = new Error(`No variants in response from ${url}`);
      } catch (error) {
        lastError = error;
      }
    }

    if (lastError) {
      console.warn('Curtain Customizer: product.js fetch failed', lastError.message);
    }
    return null;
  }

  loadFullVariantsMapping() {
    const handle = this.resolveProductHandle();
    if (!handle) {
      this.variantsFetchSettled = true;
      console.warn('Curtain Customizer: no product handle — cannot load variants for pricing');
      return Promise.resolve(false);
    }

    if (this.variantsLoadPromise) {
      return this.variantsLoadPromise;
    }

    this.variantsLoadPromise = (async () => {
        let entries = [];
        let source = '';
        let widthIdx = this.widthOptionIndex;
        let headingIdx = this.headingOptionIndex;
        let widthSkuCount = 0;

        const metafieldEntries = this.getMetafieldCatalogEntries();
        if (metafieldEntries.length) {
          entries = metafieldEntries;
          source = 'variant_price_catalog-metafield';
        }

        if (!entries.length || this.isCatalogLikelyIncomplete(entries, 191)) {
          const storefrontResult = await this.fetchAllVariantsStorefront(handle);
          if (storefrontResult && storefrontResult.entries.length > entries.length) {
            entries = storefrontResult.entries;
            source = storefrontResult.source;
          }
        }

        const productResult = await this.fetchProductJson(handle);
        if (productResult) {
          const { product } = productResult;
          const indices = this.detectOptionIndicesFromProduct(product);
          if (indices.widthIdx) widthIdx = indices.widthIdx;
          if (indices.headingIdx) headingIdx = indices.headingIdx;

          widthSkuCount = this.getWidthSkuCountFromProduct(product);

          if (!entries.length) {
            const jsEntries = this.mapProductJsVariantsToEntries(product, widthIdx, headingIdx);
            if (jsEntries.length) {
              entries = jsEntries;
              source = 'product.js';
            }
          }
        }

        if (!widthIdx) {
          console.warn('Curtain Customizer: could not detect Width option on product');
          return false;
        }

        this.widthOptionIndex = widthIdx;
        if (headingIdx) this.headingOptionIndex = headingIdx;

        if (!entries.length) return false;

        this.buildVariantsMapFromEntries(entries);
        const mapSize = Object.keys(this.variantsMap).length;
        const maxWidth = this.getMaxCatalogWidthCm();
        if (mapSize > 0) {
          console.info(
            `Curtain Customizer: loaded ${mapSize} variant prices (max width ${maxWidth}cm) via ${source || 'unknown'}`
          );
        }
        if (this.isCatalogLikelyIncomplete(entries, widthSkuCount)) {
          console.warn(
            'Curtain Customizer: only',
            entries.length,
            'variants loaded (max',
            maxWidth,
            'cm). Shopify caps Liquid and product.js at 250. Add a Storefront API token in the Curtain Customizer section settings, or populate product metafield custom.variant_price_catalog.'
          );
        }
        return mapSize > 0;
      })()
      .catch((error) => {
        console.error('Curtain Customizer: failed to load product variants', error);
        return false;
      })
      .finally(() => {
        this.variantsFetchSettled = true;
        this.updateProductConfigErrorVisibility();
        this.updatePrice();
        this.updateReviewMeasurementPreview();
      });

    return this.variantsLoadPromise;
  }

  setupEventListeners() {
    // Heading style selection
    const headingStyleOptions = this.container.querySelectorAll('.heading-style-option');
    headingStyleOptions.forEach(option => {
      option.addEventListener('click', () => this.selectHeadingStyle(option));
    });

    // Panel type selection
    const panelTypeOptions = this.container.querySelectorAll('.panel-type-option');
    panelTypeOptions.forEach(option => {
      option.addEventListener('click', () => this.selectPanelType(option));
    });

    // Join type selection (conditional section)
    const joinTypeOptions = this.container.querySelectorAll('.join-type-option');
    joinTypeOptions.forEach(option => {
      option.addEventListener('click', () => this.selectJoinType(option));
    });

    // Fullness selection
    const fullnessOptions = this.container.querySelectorAll('.fullness-option');
    fullnessOptions.forEach(option => {
      option.addEventListener('click', () => this.selectFullness(option));
    });

    // Eyelet color selection
    const eyeletOptions = this.container.querySelectorAll('.eyelet-option');
    eyeletOptions.forEach(option => {
      option.addEventListener('click', () => this.selectEyeletColor(option));
    });

    const waveHooksInput = this.container.querySelector(`#wave-hooks-input-${this.sectionId}`);
    if (waveHooksInput) {
      waveHooksInput.addEventListener('input', () => this.onWaveHookCountInput());
    }

    const waveHooksConfirm = this.container.querySelector(`#wave-hooks-confirm-${this.sectionId}`);
    if (waveHooksConfirm) {
      waveHooksConfirm.addEventListener('change', (e) => {
        this.formData.waveHooksMismatchAcknowledged = e.target.checked;
        this.validateHeadingStep();
      });
    }

    // Dimension inputs
    const widthInput = this.container.querySelector(`#width-input-${this.sectionId}`);
    const dropInput = this.container.querySelector(`#drop-input-${this.sectionId}`);
    
    // Clear any default values on initialization
    if (widthInput) {
      widthInput.value = '';
      widthInput.addEventListener('input', (e) => {
        const parsed = this.parseDimensionInputValue(e.target.value);
        if (e.target.value !== parsed.displayValue) {
          e.target.value = parsed.displayValue;
        }
        this.formData.width = parsed.value;
        this.setDimensionFieldInvalidState(e.target, parsed.isInvalid);
        
        // Show error if width exceeds 8.00m
        const widthError = this.container.querySelector(`#width-error-${this.sectionId}`);
        if (parsed.value > this.maxWidthMeters) {
          if (widthError) widthError.style.display = 'block';
        } else {
          if (widthError) widthError.style.display = 'none';
        }
        
        this.validateDimensionsStep();
        this.updatePrice();
        if (this.isWaveHeading()) {
          this.updateWaveHooksUI();
          this.validateHeadingStep();
        }
        this.updateReviewMeasurementPreview();
      });
    }
    if (dropInput) {
      dropInput.value = '';
      dropInput.addEventListener('input', (e) => {
        const parsed = this.parseDimensionInputValue(e.target.value);
        if (e.target.value !== parsed.displayValue) {
          e.target.value = parsed.displayValue;
        }
        this.formData.drop = parsed.value;
        this.setDimensionFieldInvalidState(e.target, parsed.isInvalid);
        
        // Show error if drop exceeds 2.70m
        const dropError = this.container.querySelector(`#drop-error-${this.sectionId}`);
        if (parsed.value > this.maxDropMeters) {
          if (dropError) dropError.style.display = 'block';
        } else {
          if (dropError) dropError.style.display = 'none';
        }
        
        this.validateDimensionsStep();
        this.updatePrice();
        this.updateReviewMeasurementPreview();
      });
    }

    // Room name input
    const roomNameInput = this.container.querySelector(`#room-name-input-${this.sectionId}`);
    if (roomNameInput) {
      const syncRoomName = (e) => {
        this.formData.roomName = e.target.value.trim();
        roomNameInput.classList.remove('room-name-input__field--attention');
        this.validateReviewStep();
      };
      roomNameInput.addEventListener('input', syncRoomName);
      roomNameInput.addEventListener('change', syncRoomName);
      roomNameInput.addEventListener('blur', syncRoomName);
    }

    // Navigation buttons
    const nextButtons = this.container.querySelectorAll('.curtain-customizer__next');
    nextButtons.forEach(btn => {
      btn.addEventListener('click', () => this.nextStep());
    });

    const prevButtons = this.container.querySelectorAll('.curtain-customizer__prev');
    prevButtons.forEach(btn => {
      btn.addEventListener('click', () => this.prevStep());
    });

    // Form submission
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.addToCart();
    });

    // Add another room button
    const addAnotherBtn = this.container.querySelector('.curtain-customizer__add-another');
    if (addAnotherBtn) {
      addAnotherBtn.addEventListener('click', () => this.resetForm());
    }

    const restartBtn = this.container.querySelector(`#restart-customizer-btn-${this.sectionId}`);
    if (restartBtn) {
      restartBtn.addEventListener('click', () => this.resetForm());
    }
  }

  setupCushionUpsellUI() {
    if (!this.cushionUpsell || !this.cushionUpsell.enabled) return;

    const checkbox = this.container.querySelector(`#cushion-upsell-checkbox-${this.sectionId}`);
    if (checkbox) {
      checkbox.addEventListener('change', () => {
        this.formData.addScatterCushions = checkbox.checked;
        this.updatePrice();
      });
    }

    const successBtn = this.container.querySelector(`#success-add-cushions-btn-${this.sectionId}`);
    if (successBtn) {
      successBtn.addEventListener('click', () => this.addCushionFromSuccess());
    }

    const tooltipTrigger = this.container.querySelector(`#cushion-tooltip-trigger-${this.sectionId}`);
    const tooltipPanel = this.container.querySelector(`#cushion-tooltip-panel-${this.sectionId}`);
    const tooltipWrap = tooltipTrigger ? tooltipTrigger.closest('.cc-tooltip') : null;
    if (tooltipTrigger && tooltipPanel && tooltipWrap) {
      const toggle = (open) => {
        const isOpen = typeof open === 'boolean' ? open : !tooltipWrap.classList.contains('is-open');
        tooltipWrap.classList.toggle('is-open', isOpen);
        tooltipPanel.hidden = !isOpen;
        tooltipTrigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      };
      tooltipTrigger.addEventListener('click', (e) => {
        e.stopPropagation();
        toggle();
      });
      document.addEventListener('click', (e) => {
        if (!tooltipWrap.contains(e.target)) toggle(false);
      });
      tooltipTrigger.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') toggle(false);
      });
    }
  }

  showToast(message) {
    const el = this.container.querySelector(`#cc-toast-${this.sectionId}`);
    if (!el || !message) return;
    el.textContent = message;
    el.hidden = false;
    requestAnimationFrame(() => {
      el.classList.add('is-visible');
    });
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => {
      el.classList.remove('is-visible');
      setTimeout(() => {
        el.hidden = true;
      }, 350);
    }, 3800);
  }

  updateSuccessCushionUI() {
    if (!this.cushionUpsell || !this.cushionUpsell.enabled) return;
    const block = this.container.querySelector(`#success-cushion-block-${this.sectionId}`);
    const actions = this.container.querySelector(`#success-cushion-actions-${this.sectionId}`);
    const already = this.container.querySelector(`#success-cushion-already-${this.sectionId}`);

    if (!this.cushionsAddedWithCurtain) {
      if (block) block.style.display = '';
      if (actions) actions.style.display = '';
      if (already) {
        already.textContent = '';
        already.style.display = 'none';
      }
      return;
    }

    // Cushions already in cart (review bundle or success-screen add) — hide row; toast only for success-screen add
    if (block) block.style.display = 'none';
  }

  async fetchCartResource() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        }
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.warn('Curtain Customizer: failed to fetch cart resource', error);
      return null;
    }
  }

  async notifyCartUpdated(options = {}) {
    const cart = await this.fetchCartResource();
    const itemCount = Number.isFinite(options.itemCount)
      ? options.itemCount
      : (cart && Number.isFinite(cart.item_count) ? cart.item_count : undefined);
    const detail = {
      resource: cart || null,
      sourceId: `curtain-customizer-${this.sectionId}`,
      data: {
        source: 'curtain-customizer',
        itemCount
      }
    };

    // Theme-native cart event consumed by product/cart components.
    document.dispatchEvent(new CustomEvent('cart:update', { detail }));
    // Backward-compatible event for existing custom listeners.
    document.dispatchEvent(new CustomEvent('cart:updated', { detail }));
  }

  async addCushionFromSuccess() {
    if (!this.cushionUpsell || !this.cushionUpsell.variantId || this.isAddingCushionFromSuccess) return;
    this.isAddingCushionFromSuccess = true;
    const btn = this.container.querySelector(`#success-add-cushions-btn-${this.sectionId}`);
    if (btn) btn.disabled = true;
    try {
      const vid = String(this.cushionUpsell.variantId).trim();
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: vid,
            quantity: 1
          }]
        })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || 'Could not add cushions to cart');
      }
      // Toast only when cushions were not already added with the curtain on the review step
      const showCushionAddedToast = !this.cushionsAddedWithCurtain;
      this.cushionsAddedWithCurtain = true;
      this.updateSuccessCushionUI();
      await this.notifyCartUpdated();
      if (showCushionAddedToast) {
        this.showToast(this.cushionUpsell.toastMessage || 'Matching cushions added to your cart.');
      }
    } catch (err) {
      console.error(err);
      this.showError(err.message || 'Could not add cushions.');
    } finally {
      this.isAddingCushionFromSuccess = false;
      if (btn) btn.disabled = false;
    }
  }

  selectHeadingStyle(option) {
    const selectedName = (option.dataset.headingStyleName || '').toLowerCase();
    const isWaveSelection = selectedName.includes('wave');

    // Remove previous selection
    const allOptions = this.container.querySelectorAll('.heading-style-option');
    allOptions.forEach(opt => opt.classList.remove('selected'));

    // Add selection
    option.classList.add('selected');

    // Store data
    this.formData.headingStyle = {
      id: option.dataset.headingStyleId,
      name: option.dataset.headingStyleName,
      handle: option.dataset.headingStyleHandle || null,
      shopifyHeadingValue: option.dataset.shopifyHeadingValue || null
    };

    // Update hidden inputs
    const headingStyleIdInput = this.container.querySelector(`#heading-style-id-${this.sectionId}`);
    const headingStyleNameInput = this.container.querySelector(`#heading-style-name-${this.sectionId}`);
    if (headingStyleIdInput) headingStyleIdInput.value = this.formData.headingStyle.id;
    if (headingStyleNameInput) headingStyleNameInput.value = this.formData.headingStyle.name;

    // Check if eyelet is selected and update step count
    this.updateStepCountForEyelet();
    
    // Apply special constraints for Waterfall heading style
    this.applyWaterfallConstraints();
    
    // Update join type section visibility based on heading style
    this.updateJoinTypeSectionVisibility();

    // Update fullness options based on heading style (e.g. hide 2X for wave)
    this.updateFullnessOptionsForHeadingStyle();
    this.updateHeadingStyleNotice();
    this.updateWaveHooksSectionVisibility();
    
    // Keep current step in bounds if eyelet step gets removed
    if (this.currentStep > this.totalSteps) {
      this.currentStep = this.totalSteps;
      this.showStep(this.currentStep);
    }

    this.validateHeadingStep();
    this.updateStepIndicator();
    this.updatePrice();
    this.updateReviewMeasurementPreview();
  }

  checkIfEyeletSelected() {
    if (!this.formData.headingStyle || !this.formData.headingStyle.name) return false;
    return this.formData.headingStyle.name.toLowerCase().includes('eyelet');
  }

  updateStepCountForEyelet() {
    const isEyelet = this.checkIfEyeletSelected();
    this.totalSteps = isEyelet ? 5 : 4;
    
    // Update step indicator labels
    const step2Label = this.container.querySelector(`#step-2-label-${this.sectionId}`);
    const step3Label = this.container.querySelector(`#step-3-label-${this.sectionId}`);
    const step4Label = this.container.querySelector(`#step-4-label-${this.sectionId}`);
    const step5Indicator = this.container.querySelector(`#step-indicator-5-${this.sectionId}`);
    
    // Update dimensions and fullness nav copy
    const dimensionsNextBtn = this.container.querySelector(`#dimensions-next-btn-${this.sectionId}`);
    const fullnessNextBtn = this.container.querySelector(`#fullness-next-btn-${this.sectionId}`);
    const headingNextBtn = this.container.querySelector(`#heading-next-btn-${this.sectionId}`);
    const reviewPrevBtn = this.container.querySelector(`#review-prev-btn-${this.sectionId}`);
    
    if (isEyelet) {
      if (step2Label) step2Label.textContent = 'Fullness';
      if (step3Label) step3Label.textContent = 'Heading Style';
      if (step4Label) step4Label.textContent = 'Eyelet Color';
      if (step5Indicator) step5Indicator.style.display = 'flex';
      if (dimensionsNextBtn) dimensionsNextBtn.textContent = 'Next: Fullness';
      if (fullnessNextBtn) fullnessNextBtn.textContent = 'Next: Heading Style';
      if (headingNextBtn) headingNextBtn.textContent = 'Next: Eyelet Color';
      if (reviewPrevBtn) reviewPrevBtn.textContent = '← Change Eyelet Color';
    } else {
      if (step2Label) step2Label.textContent = 'Fullness';
      if (step3Label) step3Label.textContent = 'Heading Style';
      if (step4Label) step4Label.textContent = 'Review';
      if (step5Indicator) step5Indicator.style.display = 'none';
      if (dimensionsNextBtn) dimensionsNextBtn.textContent = 'Next: Fullness';
      if (fullnessNextBtn) fullnessNextBtn.textContent = 'Next: Heading Style';
      if (headingNextBtn) headingNextBtn.textContent = 'Next: Review';
      if (reviewPrevBtn) reviewPrevBtn.textContent = '← Change Heading Style';
      // Clear eyelet selection if not eyelet heading style
      this.formData.eyeletColor = null;
      const eyeletOptions = this.container.querySelectorAll('.eyelet-option');
      eyeletOptions.forEach(opt => opt.classList.remove('selected'));
    }
  }

  selectEyeletColor(option) {
    // Remove previous selection
    const allOptions = this.container.querySelectorAll('.eyelet-option');
    allOptions.forEach(opt => opt.classList.remove('selected'));

    // Add selection
    option.classList.add('selected');

    // Store data
    this.formData.eyeletColor = {
      id: option.dataset.eyeletId,
      name: option.dataset.eyeletName
    };

    // Update hidden inputs
    const eyeletIdInput = this.container.querySelector(`#eyelet-id-${this.sectionId}`);
    const eyeletNameInput = this.container.querySelector(`#eyelet-name-${this.sectionId}`);
    if (eyeletIdInput) eyeletIdInput.value = this.formData.eyeletColor.id;
    if (eyeletNameInput) eyeletNameInput.value = this.formData.eyeletColor.name;

    this.validateEyeletStep();
  }

  selectPanelType(option) {
    // Ignore clicks on disabled panel type options
    if (option.classList.contains('panel-type-option--disabled')) {
      return;
    }
    // Remove previous selection
    const allOptions = this.container.querySelectorAll('.panel-type-option');
    allOptions.forEach(opt => opt.classList.remove('selected'));

    // Add selection
    option.classList.add('selected');

    // Store data
    this.formData.panelType = option.dataset.panelType;

    // Update join type section visibility based on panel type
    this.updateJoinTypeSectionVisibility();

    this.validateDimensionsStep();
    this.updateReviewMeasurementPreview();
  }

  selectFullness(option) {
    // Remove previous selection
    const allOptions = this.container.querySelectorAll('.fullness-option');
    allOptions.forEach(opt => opt.classList.remove('selected'));

    // Add selection
    option.classList.add('selected');

    // Store data
    this.formData.fullness = {
      id: option.dataset.fullnessId,
      name: option.dataset.fullnessName,
      value: parseFloat(option.dataset.fullnessValue)
    };

    // Update hidden inputs
    const fullnessIdInput = this.container.querySelector(`#fullness-id-${this.sectionId}`);
    const fullnessNameInput = this.container.querySelector(`#fullness-name-${this.sectionId}`);
    const fullnessValueInput = this.container.querySelector(`#fullness-value-${this.sectionId}`);
    if (fullnessIdInput) fullnessIdInput.value = this.formData.fullness.id;
    if (fullnessNameInput) fullnessNameInput.value = this.formData.fullness.name;
    if (fullnessValueInput) fullnessValueInput.value = this.formData.fullness.value;

    // Update price
    this.updatePrice();

    // Validate width × fullness doesn't exceed 2000cm
    this.validateFullnessWidth();

    this.validateFullnessStep();
    if (this.isWaveHeading()) {
      this.updateWaveHooksUI();
      this.validateHeadingStep();
    }
    this.updateReviewMeasurementPreview();
  }

  selectJoinType(option) {
    // Ignore clicks on disabled join type options
    if (option.classList.contains('join-type-option--disabled')) {
      return;
    }
    // Remove previous selection
    const allOptions = this.container.querySelectorAll('.join-type-option');
    allOptions.forEach(opt => opt.classList.remove('selected'));

    // Add selection
    option.classList.add('selected');

    // Store data
    this.formData.joinType = option.dataset.joinType;

    // Update hidden input
    const joinTypeInput = this.container.querySelector(`#join-type-${this.sectionId}`);
    if (joinTypeInput) joinTypeInput.value = this.formData.joinType;

    this.validateDimensionsStep();
    this.updateReviewMeasurementPreview();
  }

  shouldShowJoinTypeSection() {
    // Only relevant when we have heading style and panel type
    if (!this.formData.headingStyle || !this.formData.headingStyle.name || !this.formData.panelType) return false;

    const name = this.formData.headingStyle.name.toLowerCase();
    const isTape = name.includes('double tape') || name.includes('single tape');
    const isWaterfall = name.includes('waterfall');
    const isPair = this.formData.panelType === 'pair';

    // Tape styles only show join section when pair is selected,
    // Waterfall always shows join section (panel type is forced to pair)
    return (isTape && isPair) || isWaterfall;
  }

  updateJoinTypeSectionVisibility() {
    const section = this.container.querySelector(`#join-type-section-${this.sectionId}`);
    if (!section) return; // Not rendered for non-curtain form types

    const shouldShow = this.shouldShowJoinTypeSection();

    if (shouldShow) {
      section.style.display = '';

      // Default to separate if nothing chosen yet
      if (!this.formData.joinType) {
        const defaultOption = section.querySelector('.join-type-option[data-join-type="separate"]');
        if (defaultOption) {
          this.selectJoinType(defaultOption);
        }
      }
    } else {
      section.style.display = 'none';
      this.formData.joinType = null;
      section.querySelectorAll('.join-type-option').forEach(opt => opt.classList.remove('selected'));
      const joinTypeInput = this.container.querySelector(`#join-type-${this.sectionId}`);
      if (joinTypeInput) joinTypeInput.value = '';
    }
  }

  isWaterfallHeading() {
    if (!this.formData.headingStyle || !this.formData.headingStyle.name) return false;
    return this.formData.headingStyle.name.toLowerCase().includes('waterfall');
  }

  isWaveHeading() {
    const headingStyle = this.formData.headingStyle;
    if (!headingStyle) return false;
    return (
      (headingStyle.handle && headingStyle.handle.toLowerCase() === 'wave') ||
      (!headingStyle.handle && headingStyle.name && headingStyle.name.toLowerCase().includes('wave'))
    );
  }

  getWaveWidthFromHooksCm(hooks) {
    const count = parseInt(hooks, 10);
    if (!Number.isFinite(count) || count < 2) return 0;
    return (count - 1) * 12 + 10;
  }

  getEstimatedWidthCm() {
    return this.getTotalWidthWithFullnessCm();
  }

  /** Track width × fullness, rounded to 10cm — for display on review (not variant SKU). */
  getDisplayFullnessWidthCm() {
    const estimatedCm = this.getEstimatedWidthCm();
    if (!estimatedCm || estimatedCm <= 0) return 0;
    return this.roundWidthToNearest10(estimatedCm);
  }

  isWaveHookCountValid(hooks) {
    const hookCm = this.getWaveWidthFromHooksCm(hooks);
    if (!hookCm) return false;
    const variantWidth = this.isWaveHeading()
      ? this.getVariantWidthForSelection(hookCm)
      : this.roundWidthToNearest10(hookCm);
    return variantWidth >= 100 && variantWidth <= 2000;
  }

  isWaveHooksMismatch() {
    if (!this.isWaveHeading()) return false;
    const hooks = this.formData.trackHookCount;
    if (!this.isWaveHookCountValid(hooks)) return false;

    const hookCm = this.getWaveWidthFromHooksCm(hooks);
    const expectedRange = this.getWaveExpectedWidthRangeCm();
    if (!expectedRange || !hookCm) return false;

    const tolerance = this.waveWidthMismatchTolerance;
    const minCm = expectedRange.lowerBoundCm * (1 - tolerance);
    const maxCm = expectedRange.upperBoundCm * (1 + tolerance);
    return hookCm < minCm || hookCm > maxCm;
  }

  getEffectiveWidthCm() {
    if (this.isWaveHeading() && this.isWaveHookCountValid(this.formData.trackHookCount)) {
      return this.getWaveWidthFromHooksCm(this.formData.trackHookCount);
    }
    return this.getEstimatedWidthCm();
  }

  clearWaveHooks() {
    this.formData.trackHookCount = null;
    this.formData.waveHooksMismatchAcknowledged = false;

    const hookInput = this.container.querySelector(`#wave-hooks-input-${this.sectionId}`);
    if (hookInput) hookInput.value = '';

    const confirmCheckbox = this.container.querySelector(`#wave-hooks-confirm-${this.sectionId}`);
    if (confirmCheckbox) confirmCheckbox.checked = false;

    const hiddenInput = this.container.querySelector(`#track-hook-count-${this.sectionId}`);
    if (hiddenInput) hiddenInput.value = '';

    const calculatedEl = this.container.querySelector(`#wave-hooks-calculated-${this.sectionId}`);
    if (calculatedEl) calculatedEl.textContent = '';

    const errorEl = this.container.querySelector(`#wave-hooks-error-${this.sectionId}`);
    if (errorEl) {
      errorEl.style.display = 'none';
      errorEl.textContent = '';
    }

    const mismatchEl = this.container.querySelector(`#wave-hooks-mismatch-${this.sectionId}`);
    if (mismatchEl) mismatchEl.style.display = 'none';

    const mismatchText = this.container.querySelector(`#wave-hooks-mismatch-text-${this.sectionId}`);
    if (mismatchText) mismatchText.textContent = '';
  }

  updateWaveHooksSectionVisibility() {
    const section = this.container.querySelector(`#wave-hooks-section-${this.sectionId}`);
    if (!section) return;

    if (this.isWaveHeading()) {
      section.style.display = '';
      this.updateWaveHooksUI();
    } else {
      section.style.display = 'none';
      this.clearWaveHooks();
    }
  }

  updateWaveHooksUI() {
    const hookInput = this.container.querySelector(`#wave-hooks-input-${this.sectionId}`);
    const calculatedEl = this.container.querySelector(`#wave-hooks-calculated-${this.sectionId}`);
    const errorEl = this.container.querySelector(`#wave-hooks-error-${this.sectionId}`);
    const mismatchEl = this.container.querySelector(`#wave-hooks-mismatch-${this.sectionId}`);
    const mismatchText = this.container.querySelector(`#wave-hooks-mismatch-text-${this.sectionId}`);
    const confirmCheckbox = this.container.querySelector(`#wave-hooks-confirm-${this.sectionId}`);
    const hiddenInput = this.container.querySelector(`#track-hook-count-${this.sectionId}`);

    const rawValue = hookInput ? hookInput.value.trim() : '';
    const parsed = rawValue === '' ? null : parseInt(rawValue, 10);

    if (rawValue === '' || !Number.isFinite(parsed)) {
      this.formData.trackHookCount = null;
      if (hiddenInput) hiddenInput.value = '';
      if (calculatedEl) calculatedEl.textContent = '';
      if (errorEl) {
        errorEl.style.display = 'none';
        errorEl.textContent = '';
      }
      if (mismatchEl) mismatchEl.style.display = 'none';
      this.formData.waveHooksMismatchAcknowledged = false;
      if (confirmCheckbox) confirmCheckbox.checked = false;
      return;
    }

    this.formData.trackHookCount = parsed;
    if (hiddenInput) hiddenInput.value = String(parsed);

    const hookCm = this.getWaveWidthFromHooksCm(parsed);
    const catalogWidthCm = this.getVariantWidthForSelection(hookCm);
    const isValid = this.isWaveHookCountValid(parsed);

    if (calculatedEl) {
      if (isValid) {
        const formulaMeters = hookCm / 100;
        let calculatedText = `${this.waveHooksCalculatedLabel}: ${this.formatMetersValue(formulaMeters)} m`;
        if (catalogWidthCm && catalogWidthCm !== this.roundWidthToNearest10(hookCm)) {
          calculatedText += ` (priced at ${this.formatMetersValue(catalogWidthCm / 100)} m)`;
        }
        calculatedEl.textContent = calculatedText;
      } else if (hookCm > 0) {
        calculatedEl.textContent = '';
      } else {
        calculatedEl.textContent = '';
      }
    }

    if (errorEl) {
      if (!isValid && parsed >= 2) {
        errorEl.textContent = this.waveHooksRangeError;
        errorEl.style.display = 'block';
      } else if (!isValid && parsed < 2) {
        errorEl.textContent = 'Enter at least 2 hooks.';
        errorEl.style.display = 'block';
      } else {
        errorEl.style.display = 'none';
        errorEl.textContent = '';
      }
    }

    const hasMismatch = isValid && this.isWaveHooksMismatch();
    if (mismatchEl && mismatchText) {
      if (hasMismatch) {
        mismatchText.textContent = this.waveHooksMismatchWarning;
        mismatchEl.style.display = 'block';
      } else {
        mismatchEl.style.display = 'none';
        mismatchText.textContent = '';
        this.formData.waveHooksMismatchAcknowledged = false;
        if (confirmCheckbox) confirmCheckbox.checked = false;
      }
    }

    if (!hasMismatch) {
      this.formData.waveHooksMismatchAcknowledged = false;
    }
  }

  onWaveHookCountInput() {
    this.formData.waveHooksMismatchAcknowledged = false;
    const confirmCheckbox = this.container.querySelector(`#wave-hooks-confirm-${this.sectionId}`);
    if (confirmCheckbox) confirmCheckbox.checked = false;
    this.updateWaveHooksUI();
    this.validateHeadingStep();
    this.updatePrice();
    this.updateReviewMeasurementPreview();
  }

  isHeadingStepValid() {
    if (!this.formData.headingStyle) return false;
    if (!this.isWaveHeading()) return true;
    if (!this.isWaveHookCountValid(this.formData.trackHookCount)) return false;
    if (this.isWaveHooksMismatch() && !this.formData.waveHooksMismatchAcknowledged) return false;
    return true;
  }

  applyWaterfallConstraints() {
    const isWaterfall = this.isWaterfallHeading();

    const singleOption = this.container.querySelector('.panel-type-option[data-panel-type="single"]');
    const pairOption = this.container.querySelector('.panel-type-option[data-panel-type="pair"]');
    const joinSection = this.container.querySelector(`#join-type-section-${this.sectionId}`);

    if (isWaterfall) {
      // Disable single panel type and force pair
      if (singleOption) {
        singleOption.classList.add('panel-type-option--disabled');
        singleOption.setAttribute('aria-disabled', 'true');
        singleOption.classList.remove('selected');
      }
      if (pairOption) {
        // Use existing selection logic (will also update validation and join section)
        this.selectPanelType(pairOption);
      }

      // Ensure join section is visible
      this.updateJoinTypeSectionVisibility();

      if (joinSection) {
        const separateOption = joinSection.querySelector('.join-type-option[data-join-type="separate"]');
        const joinedOption = joinSection.querySelector('.join-type-option[data-join-type="joined"]');

        // Disable separate option
        if (separateOption) {
          separateOption.classList.add('join-type-option--disabled');
          separateOption.setAttribute('aria-disabled', 'true');
          separateOption.classList.remove('selected');
        }

        // Force joined selection
        if (joinedOption) {
          this.selectJoinType(joinedOption);
        }
      }
    } else {
      // Re-enable panel type options when not Waterfall
      if (singleOption) {
        singleOption.classList.remove('panel-type-option--disabled');
        singleOption.removeAttribute('aria-disabled');
      }
      if (pairOption) {
        pairOption.classList.remove('panel-type-option--disabled');
        pairOption.removeAttribute('aria-disabled');
      }

      // Re-enable join type options
      if (joinSection) {
        joinSection.querySelectorAll('.join-type-option').forEach(opt => {
          opt.classList.remove('join-type-option--disabled');
          opt.removeAttribute('aria-disabled');
        });
      }
    }
    this.updateHeadingStyleNotice();
    this.updateReviewMeasurementPreview();
  }

  validateFullnessWidth() {
    if (!this.formData.width || !this.formData.fullness) return;
    
    const totalWidthCm = this.getTotalWidthWithFullnessCm();
    const variantWidth = this.roundWidthToNearest10(totalWidthCm);
    
    // Show error if exceeds 2000cm (20.00m after fullness)
    const fullnessError = this.container.querySelector(`#fullness-error-${this.sectionId}`);
    if (variantWidth > 2000) {
      if (!fullnessError) {
        // Create error element if it doesn't exist
        const fullnessGrid = this.container.querySelector(`#fullness-grid-${this.sectionId}`);
        if (fullnessGrid) {
          const errorDiv = document.createElement('div');
          errorDiv.id = `fullness-error-${this.sectionId}`;
          errorDiv.className = 'dimension-input__error';
          errorDiv.style.cssText = 'display: block; color: #f44336; font-size: 0.875rem; margin-top: 1rem; padding: 0.75rem; background: #ffebee; border-radius: 8px;';
          errorDiv.textContent = `The selected width (${this.formatMetersValue(this.formData.width)}m) and fullness (${this.formData.fullness.name}) combination results in a total width exceeding 20.00m. Please reduce the width or select a lower fullness.`;
          fullnessGrid.parentNode.insertBefore(errorDiv, fullnessGrid.nextSibling);
        }
      } else {
        fullnessError.style.display = 'block';
        fullnessError.textContent = `The selected width (${this.formatMetersValue(this.formData.width)}m) and fullness (${this.formData.fullness.name}) combination results in a total width exceeding 20.00m. Please reduce the width or select a lower fullness.`;
      }
    } else {
      if (fullnessError) {
        fullnessError.style.display = 'none';
      }
    }
  }

  getStepSequence() {
    const sequence = ['dimensions', 'fullness', 'heading'];
    if (this.checkIfEyeletSelected()) {
      sequence.push('eyelet');
    }
    sequence.push('review');
    return sequence;
  }

  getCurrentStepKey() {
    return this.getStepSequence()[this.currentStep - 1] || 'dimensions';
  }

  isDimensionsValid() {
    const joinSection = this.container.querySelector(`#join-type-section-${this.sectionId}`);
    const joinRequired = joinSection && joinSection.style.display !== 'none';
    return this.formData.width > 0 &&
      this.formData.width <= this.maxWidthMeters &&
      this.formData.drop > 0 &&
      this.formData.drop <= this.maxDropMeters &&
      this.formData.panelType !== null &&
      (!joinRequired || this.formData.joinType !== null);
  }

  isFullnessValid() {
    if (!this.formData.fullness) return false;
    const variantWidth = this.roundWidthToNearest10(this.getTotalWidthWithFullnessCm());
    return variantWidth <= 2000;
  }

  validateDimensionsStep() {
    const nextBtn = this.container.querySelector(`#dimensions-step-${this.sectionId} .curtain-customizer__next`);
    if (nextBtn) nextBtn.disabled = !this.isDimensionsValid();
  }

  validateFullnessStep() {
    const nextBtn = this.container.querySelector(`#fullness-step-${this.sectionId} .curtain-customizer__next`);
    if (nextBtn) nextBtn.disabled = !this.isFullnessValid();
  }

  validateHeadingStep() {
    const nextBtn = this.container.querySelector(`#heading-next-btn-${this.sectionId}`);
    if (nextBtn) nextBtn.disabled = !this.isHeadingStepValid();
  }

  validateEyeletStep() {
    const nextBtn = this.container.querySelector(`#eyelet-step-${this.sectionId} .curtain-customizer__next`);
    if (nextBtn) nextBtn.disabled = !this.formData.eyeletColor;
  }

  validateReviewStep() {
    const addToCartBtn = this.container.querySelector(`#review-step-${this.sectionId} #add-to-cart-btn-${this.sectionId}`);
    if (!addToCartBtn) return;
    const hasRoomName = !!(this.formData.roomName && this.formData.roomName.length > 0);
    // Keep CTA clickable; guide user to missing field on click.
    addToCartBtn.disabled = false;
    addToCartBtn.classList.toggle('btn--needs-input', !hasRoomName);
    addToCartBtn.setAttribute('aria-disabled', hasRoomName ? 'false' : 'true');
  }

  nextStep() {
    if (this.currentStep >= this.totalSteps) return;
    if (this.validateCurrentStep()) {
      this.currentStep++;
      this.showStep(this.currentStep);
      this.updateStepIndicator();
      this.updateSummary();
    }
  }

  prevStep() {
    if (this.currentStep <= 1) return;
    this.currentStep--;
    this.showStep(this.currentStep);
    this.updateStepIndicator();
  }

  validateCurrentStep() {
    const stepKey = this.getCurrentStepKey();
    if (stepKey === 'dimensions') return this.isDimensionsValid();
    if (stepKey === 'fullness') return this.isFullnessValid();
    if (stepKey === 'heading') return this.isHeadingStepValid();
    if (stepKey === 'eyelet') return this.formData.eyeletColor !== null;
    if (stepKey === 'review') return this.formData.roomName && this.formData.roomName.length > 0;
    return true;
  }

  showStep(stepNumber, options = {}) {
    const { skipScroll = false } = options;
    // Hide all steps
    this.steps.forEach(step => {
      step.style.display = 'none';
    });

    const stepKey = this.getStepSequence()[stepNumber - 1];
    let stepElement = null;
    if (stepKey === 'dimensions') stepElement = this.container.querySelector(`#dimensions-step-${this.sectionId}`);
    if (stepKey === 'fullness') stepElement = this.container.querySelector(`#fullness-step-${this.sectionId}`);
    if (stepKey === 'heading') stepElement = this.container.querySelector(`.curtain-customizer__step[data-step="3"]`);
    if (stepKey === 'eyelet') stepElement = this.container.querySelector(`#eyelet-step-${this.sectionId}`);
    if (stepKey === 'review') stepElement = this.container.querySelector(`#review-step-${this.sectionId}`);

    if (stepElement) {
      stepElement.style.display = 'block';

      // When showing eyelet step, default-select "Chosen for You" if present and no selection yet
      if (stepKey === 'eyelet' && !this.formData.eyeletColor) {
        const defaultOption = this.container.querySelector('.eyelet-option[data-default-chosen="true"]');
        if (defaultOption) this.selectEyeletColor(defaultOption);
      }

      // Scroll to top of form only after user-driven navigation.
      if (!skipScroll) {
        stepElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    if (stepKey === 'review') {
      const roomNameInput = this.container.querySelector(`#room-name-input-${this.sectionId}`);
      if (roomNameInput) {
        this.formData.roomName = roomNameInput.value.trim();
        this.validateReviewStep();
      }
    }

    this.updateLivePriceVisibility();
    this.updateReviewMeasurementPreview();
  }

  sortFullnessOptions() {
    const fullnessGrid = this.container.querySelector(`#fullness-grid-${this.sectionId}`);
    if (!fullnessGrid) return;

    const options = Array.from(fullnessGrid.querySelectorAll('.fullness-option'));
    
    // Sort by numeric value (ascending)
    options.sort((a, b) => {
      const valueA = parseFloat(a.dataset.fullnessValue) || 0;
      const valueB = parseFloat(b.dataset.fullnessValue) || 0;
      return valueA - valueB;
    });

    // Re-append in sorted order
    options.forEach(option => fullnessGrid.appendChild(option));
  }

  sortHeadingOptions() {
    const headingGrid = this.container.querySelector(`#heading-style-grid-${this.sectionId}`);
    if (!headingGrid) return;

    const options = Array.from(headingGrid.querySelectorAll('.heading-style-option'));
    if (!options.length) return;

    const formType = String(this.form.dataset.formType || '').toLowerCase();
    const priority = formType === 'sheer'
      ? ['taped', 'pocket', 'eyelet', 'wave']
      : ['taped', 'eyelet', 'wave'];

    options.sort((a, b) => {
      const keyA = this.normalizeHeadingOptionValue(a.dataset.shopifyHeadingValue || a.dataset.headingStyleName || a.dataset.headingStyleHandle);
      const keyB = this.normalizeHeadingOptionValue(b.dataset.shopifyHeadingValue || b.dataset.headingStyleName || b.dataset.headingStyleHandle);
      const indexA = priority.indexOf(keyA);
      const indexB = priority.indexOf(keyB);
      const rankA = indexA === -1 ? 999 : indexA;
      const rankB = indexB === -1 ? 999 : indexB;
      return rankA - rankB;
    });

    options.forEach((option) => headingGrid.appendChild(option));
  }

  updateStepIndicator() {
    const reviewStep = this.totalSteps;
    
    this.stepIndicators.forEach((indicator, index) => {
      const stepNumber = index + 1;
      indicator.classList.remove('active', 'completed');
      
      // Skip step 5 indicator if eyelet not selected
      if (stepNumber > this.totalSteps) {
        return;
      }
      
      // Determine if step should be active or completed
      if (stepNumber === this.currentStep && !this.isStep4Completed) {
        indicator.classList.add('active');
      } else if (stepNumber < this.currentStep || (stepNumber === reviewStep && this.isStep4Completed)) {
        indicator.classList.add('completed');
      }
    });
  }

  // Round width up to nearest 50cm
  setDefaultFullness() {
    // Find and select 2x fullness as default
    const fullnessOptions = this.container.querySelectorAll('.fullness-option');
    fullnessOptions.forEach(option => {
      const fullnessValue = parseFloat(option.dataset.fullnessValue);
      if (fullnessValue === 2) {
        // Automatically select 2x fullness
        this.selectFullness(option);
      }
    });
  }

  setDefaultHeadingStyle() {
    if (this.formData.headingStyle) return;
    const options = Array.from(this.container.querySelectorAll('.heading-style-option'));
    if (!options.length) return;
    const tapedOption = options.find((option) => {
      const key = this.normalizeHeadingOptionValue(option.dataset.shopifyHeadingValue || option.dataset.headingStyleName || option.dataset.headingStyleHandle);
      return key === 'taped';
    });
    if (tapedOption) {
      this.selectHeadingStyle(tapedOption);
    }
  }

  updateFullnessOptionsForHeadingStyle() {
    const twoXOptions = this.container.querySelectorAll('.fullness-option[data-fullness-name="2X"]');
    if (!twoXOptions.length) return;

    const headingStyle = this.formData.headingStyle;
    const isWaveHeading =
      !!headingStyle &&
      (
        (headingStyle.handle && headingStyle.handle.toLowerCase() === 'wave') ||
        (!headingStyle.handle && headingStyle.name && headingStyle.name.toLowerCase().includes('wave'))
      );

    let waveAutoAdjusted = false;

    twoXOptions.forEach(option => {
      if (isWaveHeading) {
        // Hide 2X option for wave heading style
        option.style.display = 'none';

        // If this option is currently selected, clear selection and related data
        if (option.classList.contains('selected')) {
          option.classList.remove('selected');

          if (this.formData.fullness && this.formData.fullness.id === option.dataset.fullnessId) {
            const option25x = this.container.querySelector('.fullness-option[data-fullness-name="2.5X"]') ||
              this.container.querySelector('.fullness-option[data-fullness-value="2.5"]');
            if (option25x) {
              this.selectFullness(option25x);
              waveAutoAdjusted = true;
            } else {
              this.formData.fullness = null;
              const fullnessIdInput = this.container.querySelector(`#fullness-id-${this.sectionId}`);
              const fullnessNameInput = this.container.querySelector(`#fullness-name-${this.sectionId}`);
              const fullnessValueInput = this.container.querySelector(`#fullness-value-${this.sectionId}`);
              if (fullnessIdInput) fullnessIdInput.value = '';
              if (fullnessNameInput) fullnessNameInput.value = '';
              if (fullnessValueInput) fullnessValueInput.value = '';
              this.updatePrice();
              this.validateFullnessStep();
            }
          }
        }
      } else {
        // Ensure 2X option is visible for non-wave heading styles
        option.style.display = '';
      }
    });

    if (waveAutoAdjusted) {
      this.showHeadingStepNotice('', '');
    }
  }

  roundWidthToNearest50(width) {
    if (!width || width <= 0) return 0;
    return Math.ceil(width / 50) * 50;
  }

  roundWidthToNearest10(width) {
    if (!width || width <= 0) return 0;
    return Math.ceil(width / 10) * 10;
  }

  getWaveExpectedWidthRangeCm() {
    if (!this.formData.width || this.formData.width <= 0) return null;
    const trackCm = this.formData.width * 100;
    const lowerBoundCm = trackCm * 2.5;
    const upperBoundCm = trackCm * 3.2;
    return { lowerBoundCm, upperBoundCm };
  }

  parseDimensionInputValue(rawValue) {
    const raw = typeof rawValue === 'string' ? rawValue : '';
    const cleaned = raw.replace(/[^\d.,]/g, '');
    const firstSeparatorMatch = cleaned.match(/[.,]/);
    const firstSeparator = firstSeparatorMatch ? firstSeparatorMatch[0] : null;

    let displayValue = '';
    let hasSeparator = false;
    let decimalCount = 0;

    for (const char of cleaned) {
      if (char >= '0' && char <= '9') {
        if (!hasSeparator || decimalCount < 2) {
          displayValue += char;
          if (hasSeparator) decimalCount += 1;
        }
        continue;
      }

      if ((char === '.' || char === ',') && !hasSeparator) {
        hasSeparator = true;
        displayValue += firstSeparator || '.';
      }
    }

    const trimmed = displayValue.trim();
    const separatorOnly = trimmed === '.' || trimmed === ',';
    const endsWithSeparator = /[.,]$/.test(trimmed);
    const normalized = trimmed.replace(',', '.');
    const parsedValue = separatorOnly || !trimmed ? NaN : parseFloat(normalized);
    const isInvalid = separatorOnly || (trimmed.length > 0 && Number.isNaN(parsedValue));

    return {
      displayValue,
      value: Number.isNaN(parsedValue) ? null : parsedValue,
      isInvalid,
      isPending: endsWithSeparator && !separatorOnly
    };
  }

  setDimensionFieldInvalidState(field, isInvalid) {
    if (!field) return;
    field.classList.toggle('dimension-input__field--invalid', !!isInvalid);
  }

  showHeadingStepNotice(message, noticeType = '') {
    const note = this.container.querySelector(`#heading-style-note-${this.sectionId}`);
    if (!note) return;
    this.headingNoticeType = noticeType;
    if (!message) {
      note.style.display = 'none';
      note.textContent = '';
      return;
    }
    note.textContent = message;
    note.style.display = 'block';
  }

  updateHeadingStyleNotice() {
    if (this.isWaterfallHeading()) {
      this.showHeadingStepNotice(this.waterfallForcedNote, 'waterfall');
      return;
    }

    // Keep Wave helper notice only while Wave remains selected.
    if (this.headingNoticeType === 'wave' && this.formData.headingStyle && this.formData.headingStyle.name && this.formData.headingStyle.name.toLowerCase().includes('wave')) {
      return;
    }

    this.showHeadingStepNotice('', '');
  }

  updateReviewMeasurementPreview() {
    const preview = this.container.querySelector(`#review-measurement-preview-${this.sectionId}`);
    const image = this.container.querySelector(`#review-measurement-image-${this.sectionId}`);
    const dropLabel = this.container.querySelector(`#review-preview-drop-label-${this.sectionId}`);
    const topLeftLabel = this.container.querySelector(`#review-preview-top-left-label-${this.sectionId}`);
    const topRightLabel = this.container.querySelector(`#review-preview-top-right-label-${this.sectionId}`);
    if (!preview || !image || !dropLabel || !topLeftLabel || !topRightLabel) return;

    const isWaterfall = this.isWaterfallHeading();
    const isPairMode = isWaterfall || this.formData.panelType === 'pair';
    const modeClass = isWaterfall ? 'waterfall' : (isPairMode ? 'pair' : 'single');
    preview.classList.remove('review-measurement-preview--single', 'review-measurement-preview--pair', 'review-measurement-preview--waterfall');
    preview.classList.add(`review-measurement-preview--${modeClass}`);

    const nextImageSrc = isWaterfall
      ? this.reviewPreviewImages.waterfall
      : (isPairMode ? this.reviewPreviewImages.pair : this.reviewPreviewImages.single);
    if (nextImageSrc) {
      image.src = nextImageSrc;
    }

    const modePositions = this.reviewOverlayPositions[modeClass] || {};
    const setLabelPosition = (labelEl, position) => {
      if (!labelEl || !position) return;
      const x = parseFloat(position.x);
      const y = parseFloat(position.y);
      if (!Number.isNaN(x)) labelEl.style.left = `${x}%`;
      if (!Number.isNaN(y)) labelEl.style.top = `${y}%`;
    };
    setLabelPosition(dropLabel, modePositions.drop);
    setLabelPosition(topLeftLabel, modePositions.topLeft);
    setLabelPosition(topRightLabel, modePositions.topRight);

    dropLabel.textContent = this.formData.drop ? `${this.formatMetersValue(this.formData.drop)} m` : '-';

    const estimatedDisplayWidthCm = this.getDisplayFullnessWidthCm();
    const pricedWidthCm = this.isWaveHeading()
      ? this.getVariantWidthForSelection(this.getEffectiveWidthCm())
      : this.getVariantWidthForSelection(this.getEstimatedWidthCm());
    const shouldUsePricedWidth = this.isWaveHeading() || this.getHeadingVariantKey() === 'eyelet';
    const previewWidthCm = shouldUsePricedWidth && pricedWidthCm > 0
      ? pricedWidthCm
      : estimatedDisplayWidthCm;
    const totalWidthMeters = previewWidthCm > 0 ? previewWidthCm / 100 : 0;
    const fullWidthText = totalWidthMeters > 0 ? `${this.formatMetersValue(totalWidthMeters)} m` : '-';

    if (isPairMode) {
      const perPanelWidth = totalWidthMeters > 0 ? totalWidthMeters / 2 : 0;
      const panelWidthText = perPanelWidth > 0 ? `${this.formatMetersValue(perPanelWidth)} m` : '-';
      topLeftLabel.textContent = panelWidthText;
      topRightLabel.textContent = panelWidthText;
      topRightLabel.style.display = '';
    } else {
      topLeftLabel.textContent = fullWidthText;
      topRightLabel.textContent = '-';
      topRightLabel.style.display = 'none';
    }
  }

  updateLivePriceVisibility() {
    const livePriceContainer = this.container.querySelector(`#live-price-container-${this.sectionId}`);
    if (!livePriceContainer) return;

    const shouldShow =
      this.getCurrentStepKey() !== 'review' &&
      !!this.formData.width &&
      !!this.formData.drop &&
      this.formData.width > 0 &&
      this.formData.drop > 0;
    livePriceContainer.style.display = shouldShow ? 'flex' : 'none';
  }

  isKnownHeadingKey(key) {
    return key === 'taped' || key === 'eyelet' || key === 'wave' || key === 'waterfall' || key === 'pocket';
  }

  normalizeHeadingOptionValue(value) {
    const normalized = String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();
    if (!normalized) return '';
    if (normalized.includes('eyelet')) return 'eyelet';
    if (normalized.includes('wave')) return 'wave';
    if (normalized.includes('waterfall')) return 'waterfall';
    if (normalized.includes('pocket')) return 'pocket';
    if (normalized === 'tape' || normalized.includes('taped') || /\btape\b/.test(normalized)) return 'taped';
    return '';
  }

  resolveHeadingVariantKeyFromCandidates(candidates) {
    for (const candidate of candidates) {
      if (!candidate) continue;
      const key = this.normalizeHeadingOptionValue(candidate);
      if (this.isKnownHeadingKey(key)) return key;
    }

    if (this.shopifyHeadingOptions && this.shopifyHeadingOptions.length > 0) {
      for (const candidate of candidates) {
        const normalized = String(candidate || '').toLowerCase().replace(/\s+/g, ' ').trim();
        if (!normalized) continue;
        for (const optionValue of this.shopifyHeadingOptions) {
          const optionKey = this.normalizeHeadingOptionValue(optionValue);
          if (!this.isKnownHeadingKey(optionKey)) continue;
          const optionLower = String(optionValue).toLowerCase();
          if (
            normalized === optionLower ||
            normalized.includes(optionLower) ||
            optionLower.includes(normalized)
          ) {
            return optionKey;
          }
        }
      }
    }

    if (!this.productHeadingKeys || this.productHeadingKeys.size === 0) return null;

    for (const candidate of candidates) {
      const normalized = String(candidate || '').toLowerCase().replace(/\s+/g, ' ').trim();
      if (!normalized) continue;
      for (const productKey of this.productHeadingKeys) {
        if (normalized.includes(productKey) || productKey.includes(normalized)) {
          return productKey;
        }
      }
    }

    return null;
  }

  getHeadingVariantKey() {
    if (!this.formData.headingStyle) return null;

    const { name, handle, shopifyHeadingValue } = this.formData.headingStyle;

    return this.resolveHeadingVariantKeyFromCandidates([
      shopifyHeadingValue,
      name,
      handle
    ]);
  }

  buildVariantMapKey(width, headingKey) {
    if (this.hasHeadingVariants && headingKey) {
      return `${width}|${headingKey}`;
    }
    return String(width);
  }

  getTotalWidthWithFullnessCm() {
    if (!this.formData.width || !this.formData.fullness) return 0;
    const fullnessValue = parseFloat(this.formData.fullness.value);
    if (!Number.isFinite(fullnessValue) || fullnessValue <= 0) return 0;
    const widthMeters = parseFloat(this.formData.width);
    if (!Number.isFinite(widthMeters) || widthMeters <= 0) return 0;
    return widthMeters * 100 * fullnessValue;
  }

  formatMetersValue(value) {
    if (typeof value !== 'number' || Number.isNaN(value) || value <= 0) return '-';
    return value.toFixed(2);
  }

  updatePriceText(element, nextText) {
    if (!element || !nextText || element.textContent === nextText) return;
    element.classList.remove('is-price-animating');
    void element.offsetWidth;
    element.textContent = nextText;
    element.classList.add('is-price-animating');
  }

  findClosestVariantMapKey(targetCm, headingKey) {
    const targetWidth = this.snapToProductWidthGridCm(targetCm);
    const headingKeysToTry = new Set();

    if (headingKey) headingKeysToTry.add(headingKey);
    if (this.formData.headingStyle) {
      [this.formData.headingStyle.shopifyHeadingValue, this.formData.headingStyle.name, this.formData.headingStyle.handle]
        .forEach((label) => {
          const key = this.getHeadingKeyFromLabel(label);
          if (key) headingKeysToTry.add(key);
        });
    }
    this.shopifyHeadingOptions.forEach((optionValue) => {
      const key = this.getHeadingKeyFromLabel(optionValue);
      if (key) headingKeysToTry.add(key);
    });

    let bestKey = null;
    let bestWidth = Infinity;

    Object.keys(this.variantsMap).forEach((mapKey) => {
      const pipeIndex = mapKey.indexOf('|');
      const width = parseInt(pipeIndex >= 0 ? mapKey.slice(0, pipeIndex) : mapKey, 10);
      if (!Number.isFinite(width) || width < targetWidth) return;

      if (pipeIndex >= 0 && headingKeysToTry.size > 0) {
        const mapHeading = mapKey.slice(pipeIndex + 1);
        let headingMatch = false;
        headingKeysToTry.forEach((tryKey) => {
          if (mapHeading === tryKey || mapHeading.includes(tryKey) || tryKey.includes(mapHeading)) {
            headingMatch = true;
          }
        });
        if (!headingMatch) return;
      }

      if (width < bestWidth) {
        bestWidth = width;
        bestKey = mapKey;
      }
    });

    return bestKey;
  }

  findVariantForSelection(calculatedWidthCm, headingKeyOverride = undefined) {
    if (!Number.isFinite(calculatedWidthCm) || calculatedWidthCm <= 0) return null;

    let headingKey = null;
    if (headingKeyOverride !== undefined) {
      headingKey = headingKeyOverride;
    } else if (this.hasHeadingVariants) {
      headingKey = this.getHeadingVariantKey();
    }
    if (headingKey === 'waterfall') return null;

    const variantWidth = this.getVariantWidthForSelection(calculatedWidthCm, headingKey);
    if (!variantWidth) return null;

    let mapKey = this.lookupVariantMapKey(variantWidth, headingKey);
    if (!mapKey) {
      mapKey = this.findClosestVariantMapKey(calculatedWidthCm, headingKey);
    }
    if (!mapKey) return null;

    const variantId = this.variantsMap[mapKey];
    const variantData = this.variantsData[mapKey];
    if (!variantId || !variantData || variantData.price == null) return null;

    const matchedWidth = parseInt(mapKey.split('|')[0], 10) || variantWidth;

    return {
      id: variantId,
      width: matchedWidth,
      price: variantData.price
    };
  }

  isVariantsCatalogReadyForPricing() {
    if (Object.keys(this.variantsMap).length > 0) return true;
    return this.variantsFetchSettled;
  }

  updatePrice() {
    const livePriceContainer = this.container.querySelector(`#live-price-container-${this.sectionId}`);
    const livePriceElement = this.container.querySelector(`#live-price-${this.sectionId}`);
    const priceLabelEl = this.container.querySelector(`#summary-curtain-price-label-${this.sectionId}`);
    const cushionRow = this.container.querySelector(`#summary-cushion-row-${this.sectionId}`);
    const totalRow = this.container.querySelector(`#summary-total-row-${this.sectionId}`);
    const cushionPriceEl = this.container.querySelector(`#summary-cushion-price-${this.sectionId}`);
    const totalPriceEl = this.container.querySelector(`#summary-total-price-${this.sectionId}`);
    const hasUpsell =
      this.cushionUpsell &&
      this.cushionUpsell.enabled &&
      typeof this.cushionUpsell.price === 'number';
    const cushionPriceNum = hasUpsell ? this.cushionUpsell.price : 0;
    const upsellChecked = !!(hasUpsell && this.formData.addScatterCushions);

    if (this.formData.width && this.formData.drop) {
      const hasExplicitSelections = !!(this.formData.fullness && this.formData.headingStyle);
      const fallbackFullness = 2;
      const fallbackHeading = 'taped';
      const headingKeyForPricing = hasExplicitSelections
        ? (this.hasHeadingVariants ? this.getHeadingVariantKey() : fallbackHeading)
        : fallbackHeading;
      const widthForPricing = this.isWaveHeading()
        ? this.getEffectiveWidthCm()
        : this.formData.width * 100 * (hasExplicitSelections ? (this.formData.fullness?.value || fallbackFullness) : fallbackFullness);
      const variantWidth = this.getVariantWidthForSelection(widthForPricing, headingKeyForPricing);

      const priceElement = this.container.querySelector(`#summary-price-${this.sectionId}`);
      let curtainPriceNum = null;
      let priceMessage = 'Price not available';
      let matchedVariant = null;

      if (headingKeyForPricing === 'waterfall') {
        priceMessage = 'Waterfall heading is not available for this product';
      } else if (hasExplicitSelections && this.isWaveHeading() && !this.isWaveHookCountValid(this.formData.trackHookCount)) {
        priceMessage = 'Enter hook count for price';
      } else if (!this.isVariantsCatalogReadyForPricing()) {
        priceMessage = 'Loading price...';
      } else if (Object.keys(this.variantsMap).length === 0) {
        priceMessage = 'Product variants not loaded';
      } else {
        matchedVariant = this.findVariantForSelection(widthForPricing, headingKeyForPricing);
      }

      if (priceElement) {
        if (matchedVariant && matchedVariant.price != null) {
          curtainPriceNum = matchedVariant.price;
          priceMessage = this.formatPrice(matchedVariant.price);
          this.updatePriceText(priceElement, priceMessage);
        } else if (this.roundWidthToNearest10(widthForPricing) > 2000) {
          priceMessage = 'Width exceeds maximum (20.00m)';
          this.updatePriceText(priceElement, priceMessage);
        } else if (priceMessage !== 'Loading price...') {
          this.updatePriceText(priceElement, priceMessage);
        } else {
          this.updatePriceText(priceElement, priceMessage);
        }
      }

      if (livePriceContainer && livePriceElement) {
        this.updateLivePriceVisibility();
        this.updatePriceText(livePriceElement, priceMessage);
      }

      if (priceLabelEl && this.cushionUpsell && this.cushionUpsell.enabled) {
        priceLabelEl.textContent = upsellChecked
          ? (this.cushionUpsell.curtainPriceLabel || 'Curtains:')
          : (this.cushionUpsell.defaultPriceLabel || 'Price:');
      } else if (priceLabelEl) {
        priceLabelEl.textContent = 'Price:';
      }

      if (cushionRow && totalRow && cushionPriceEl && totalPriceEl && hasUpsell) {
        if (upsellChecked && curtainPriceNum != null) {
          cushionRow.style.display = 'flex';
          totalRow.style.display = 'flex';
          cushionPriceEl.textContent = this.formatPrice(cushionPriceNum);
          totalPriceEl.textContent = this.formatPrice(curtainPriceNum + cushionPriceNum);
        } else {
          cushionRow.style.display = 'none';
          totalRow.style.display = 'none';
        }
      }
      
      this.updateSummaryWidthDisplay();
    } else {
      const priceElement = this.container.querySelector(`#summary-price-${this.sectionId}`);
      if (priceElement) {
        this.updatePriceText(priceElement, 'Price not available');
      }
      if (livePriceContainer && livePriceElement) {
        this.updateLivePriceVisibility();
        this.updatePriceText(livePriceElement, '-');
      }
      if (priceLabelEl) priceLabelEl.textContent = 'Price:';
      if (cushionRow) cushionRow.style.display = 'none';
      if (totalRow) totalRow.style.display = 'none';
      this.updateSummaryWidthDisplay();
    }
  }

  updateSummaryWidthDisplay() {
    const totalWidthLabelEl = this.container.querySelector(`#summary-total-width-label-${this.sectionId}`);
    const totalWidthFullnessEl = this.container.querySelector(`#summary-total-width-fullness-${this.sectionId}`);
    const trackHooksItemEl = this.container.querySelector(`#summary-track-hooks-item-${this.sectionId}`);
    const trackHooksEl = this.container.querySelector(`#summary-track-hooks-${this.sectionId}`);

    const estimatedCm = this.getDisplayFullnessWidthCm();
    const estimatedMeters = estimatedCm > 0 ? estimatedCm / 100 : 0;
    const pricingCm = this.isWaveHeading()
      ? this.getVariantWidthForSelection(this.getEffectiveWidthCm())
      : this.getVariantWidthForSelection(estimatedCm);
    const pricingMeters = pricingCm > 0 ? pricingCm / 100 : 0;
    const usePricedWidthForSummary = this.getHeadingVariantKey() === 'eyelet';

    if (this.isWaveHeading()) {
      if (totalWidthLabelEl) totalWidthLabelEl.textContent = 'Total Width with Fullness:';
      if (trackHooksItemEl) trackHooksItemEl.style.display = 'flex';
      if (trackHooksEl) {
        trackHooksEl.textContent = this.formData.trackHookCount != null
          ? String(this.formData.trackHookCount)
          : '-';
      }
      if (totalWidthFullnessEl) {
        if (pricingMeters > 0) {
          totalWidthFullnessEl.textContent = `${this.formatMetersValue(pricingMeters)} m`;
        } else {
          totalWidthFullnessEl.textContent = '-';
        }
      }
    } else {
      if (totalWidthLabelEl) totalWidthLabelEl.textContent = 'Total Width with Fullness:';
      if (trackHooksItemEl) trackHooksItemEl.style.display = 'none';
      if (trackHooksEl) trackHooksEl.textContent = '-';
      if (totalWidthFullnessEl) {
        if (usePricedWidthForSummary && pricingMeters > 0) {
          totalWidthFullnessEl.textContent = `${this.formatMetersValue(pricingMeters)} m`;
        } else {
          totalWidthFullnessEl.textContent = estimatedMeters > 0
            ? `${this.formatMetersValue(estimatedMeters)} m`
            : '-';
        }
      }
    }
  }

  updateSummary() {
    // Update summary values
    const headingStyleEl = this.container.querySelector(`#summary-heading-style-${this.sectionId}`);
    if (headingStyleEl && this.formData.headingStyle) {
      headingStyleEl.textContent = this.formData.headingStyle.name;
    }

    // Update eyelet color (if selected)
    const eyeletItemEl = this.container.querySelector(`#summary-eyelet-item-${this.sectionId}`);
    const eyeletColorEl = this.container.querySelector(`#summary-eyelet-color-${this.sectionId}`);
    if (this.formData.eyeletColor) {
      if (eyeletItemEl) eyeletItemEl.style.display = 'flex';
      if (eyeletColorEl) eyeletColorEl.textContent = this.formData.eyeletColor.name;
    } else {
      if (eyeletItemEl) eyeletItemEl.style.display = 'none';
    }

    const widthEl = this.container.querySelector(`#summary-width-${this.sectionId}`);
    if (widthEl && this.formData.width) {
      widthEl.textContent = `${this.formatMetersValue(this.formData.width)} m`;
    }

    const dropEl = this.container.querySelector(`#summary-drop-${this.sectionId}`);
    if (dropEl && this.formData.drop) {
      dropEl.textContent = `${this.formatMetersValue(this.formData.drop)} m`;
    }

    const panelTypeEl = this.container.querySelector(`#summary-panel-type-${this.sectionId}`);
    if (panelTypeEl && this.formData.panelType) {
      panelTypeEl.textContent = this.formData.panelType === 'pair' ? 'Pair (2 Panels)' : 'Single';
    }

    // Middle of curtain (join type) - only when applicable
    const joinItemEl = this.container.querySelector(`#summary-join-type-item-${this.sectionId}`);
    const joinTypeEl = this.container.querySelector(`#summary-join-type-${this.sectionId}`);
    if (joinItemEl && joinTypeEl) {
      if (this.formData.joinType) {
        joinItemEl.style.display = 'flex';
        joinTypeEl.textContent = this.formData.joinType === 'joined' ? 'Joined' : 'Separate';
      } else {
        joinItemEl.style.display = 'none';
        joinTypeEl.textContent = '-';
      }
    }

    const fullnessEl = this.container.querySelector(`#summary-fullness-${this.sectionId}`);
    const fullnessItemEl = fullnessEl ? fullnessEl.closest('.summary-item') : null;
    if (this.isWaveHeading()) {
      if (fullnessItemEl) fullnessItemEl.style.display = 'none';
    } else if (fullnessEl) {
      if (fullnessItemEl) fullnessItemEl.style.display = 'flex';
      if (this.formData.fullness) fullnessEl.textContent = this.formData.fullness.name;
    }

    this.updateSummaryWidthDisplay();

    // Update price
    this.updatePrice();
    this.updateReviewMeasurementPreview();

    this.validateReviewStep();
  }

  formatPrice(price) {
    // Format price using shop currency
    if (typeof price !== 'number' || isNaN(price) || price === 0) {
      // Return formatted zero with shop currency
      return new Intl.NumberFormat('en-ZA', {
        style: 'currency',
        currency: this.shopCurrency || 'ZAR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(0);
    }
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: this.shopCurrency || 'ZAR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
  }

  async addToCart() {
    // Prevent double submission
    if (this.isAddingToCart) {
      console.warn('Already adding to cart, ignoring duplicate call');
      return;
    }

    const roomNameInput = this.container.querySelector(`#room-name-input-${this.sectionId}`);
    if (roomNameInput) {
      this.formData.roomName = roomNameInput.value.trim();
    }

    if (!this.formData.roomName || this.formData.roomName.length === 0) {
      if (roomNameInput) {
        roomNameInput.classList.add('room-name-input__field--attention');
        roomNameInput.focus();
      }
      this.showError('Please enter a room and curtain name before adding to cart.');
      this.validateReviewStep();
      return;
    }

    if (!this.validateCurrentStep()) {
      this.showError('Please complete all required fields.');
      return;
    }

    // Validate variants mapping is loaded
    if (!this.variantsMap || Object.keys(this.variantsMap).length === 0) {
      this.showError('Product variants are not configured. Please ensure the product has width-based variants.');
      return;
    }

    // Set flag to prevent double submission
    this.isAddingToCart = true;

    const addToCartBtn = this.container.querySelector(`#add-to-cart-btn-${this.sectionId}`);
    const btnText = addToCartBtn.querySelector('.btn__text');
    const btnLoader = addToCartBtn.querySelector('.btn__loader');
    
    // Show loading state
    addToCartBtn.disabled = true;
    if (btnText) btnText.style.display = 'none';
    if (btnLoader) btnLoader.style.display = 'inline-flex';

    try {
      if (this.isWaveHeading() && !this.isHeadingStepValid()) {
        throw new Error('Please enter a valid track hook count for Wave heading before adding to cart.');
      }

      await this.loadFullVariantsMapping();

      const estimatedWidthCm = this.getEstimatedWidthCm();
      const estimatedWidthMeters = estimatedWidthCm / 100;
      const effectiveWidthCm = this.getEffectiveWidthCm();
      const widthForPricing = this.isWaveHeading() ? effectiveWidthCm : estimatedWidthCm;

      if (this.hasHeadingVariants && !this.formData.headingStyle) {
        throw new Error('Please select a heading style before adding to cart.');
      }

      const headingKey = this.getHeadingVariantKey();
      if (this.hasHeadingVariants && headingKey === 'waterfall') {
        throw new Error('Waterfall heading is not available for this product. Please choose a different heading style.');
      }

      const matchedVariant = this.findVariantForSelection(widthForPricing);
      const variantWidth = matchedVariant ? matchedVariant.width : 0;
      const fabricWidthMeters = variantWidth > 0 ? variantWidth / 100 : effectiveWidthCm / 100;

      // Validate width range: if > 2000cm (20.00m), show error
      if (!variantWidth || variantWidth > 2000) {
        throw new Error('The calculated width exceeds the maximum available size (20.00m). Please reduce the width or fullness to proceed.');
      }
      if (!matchedVariant || !matchedVariant.id) {
        const widthLabel = this.formatMetersValue(variantWidth / 100) + 'm';
        const headingLabel = this.formData.headingStyle
          ? this.formData.headingStyle.name
          : '';
        if (this.hasHeadingVariants && headingLabel) {
          throw new Error(
            `No variant found for width ${widthLabel} with ${headingLabel} heading. Please contact support if this issue persists.`
          );
        }
        throw new Error(
          `No variant found for width ${widthLabel}. Please contact support if this issue persists.`
        );
      }
      
      // Prepare line item properties in customer-facing order:
      // Room, Panels, Fullness (if present), Fabric Width, Curtain Drop, Track Hooks (if present)
      const properties = {
        'Room': this.formData.roomName,
        'Panels': this.formData.panelType === 'pair' ? 'Pair (2 Panels)' : 'Single Panel'
      };

      if (!this.isWaveHeading()) {
        properties['Fullness'] = this.formData.fullness.name;
      }

      properties['Fabric Width'] = this.formatMetersValue(fabricWidthMeters) + ' m';
      properties['Curtain Drop'] = this.formatMetersValue(this.formData.drop) + ' m';

      if (this.isWaveHeading()) {
        properties['Track Hooks'] = String(this.formData.trackHookCount);
      }

      // Hidden/internal properties
      properties['_Track or Rod Width'] = this.formatMetersValue(this.formData.width) + ' m';
      properties['_Heading Style'] = this.formData.headingStyle.name;
      properties['_Heading Style ID'] = this.formData.headingStyle.id;
      properties['_Fullness ID'] = this.formData.fullness.id;
      properties['_Fullness'] = this.formData.fullness.name;
      properties['_Total Width with Fullness (m)'] = this.formatMetersValue(estimatedWidthMeters);

      if (this.isWaveHeading()) {
        properties['_Track Hook Count'] = String(this.formData.trackHookCount);
        properties['_Fabric Width from Hooks (m)'] = this.formatMetersValue(fabricWidthMeters);
      }

      // Add eyelet color if selected
      if (this.formData.eyeletColor) {
        properties['Eyelet Color'] = this.formData.eyeletColor.name;
        properties['_Eyelet Color ID'] = this.formData.eyeletColor.id;
      }

      // Add join type (middle of curtain) if selected
      if (this.formData.joinType) {
        properties['Middle of Curtain'] = this.formData.joinType === 'joined' ? 'Joined in the middle' : 'Separate panels';
        properties['_Join Type'] = this.formData.joinType;
      }
      
      // Add to cart using Shopify Cart API
      // Use matched variant ID and quantity 1
      const variantId = String(matchedVariant.id).trim();
      if (!variantId) {
        throw new Error('Variant ID is missing. Please configure a product in the section settings.');
      }

      const items = [{
        id: variantId,
        quantity: 1,
        properties: properties,
      }];

      if (
        this.cushionUpsell &&
        this.cushionUpsell.enabled &&
        this.formData.addScatterCushions &&
        this.cushionUpsell.variantId
      ) {
        const cushionVid = String(this.cushionUpsell.variantId).trim();
        items.push({
          id: cushionVid,
          quantity: 1
        });
      }

      const bundledCushions = !!(
        this.cushionUpsell &&
        this.cushionUpsell.enabled &&
        this.formData.addScatterCushions &&
        this.cushionUpsell.variantId
      );

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Failed to add item to cart');
      }

      this.cushionsAddedWithCurtain = bundledCushions;

      // Show success message
      this.showSuccess();

      // Trigger cart update event for theme compatibility
      await this.notifyCartUpdated();

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showError(error.message || 'An error occurred while adding the item to your cart. Please try again.');
    } finally {
      // Reset button state and submission flag
      this.isAddingToCart = false;
      if (btnText) btnText.style.display = 'inline';
      if (btnLoader) btnLoader.style.display = 'none';
      if (addToCartBtn) addToCartBtn.disabled = false;
    }
  }

  showSuccess() {
    const successMessage = this.container.querySelector(`#success-message-${this.sectionId}`);
    const form = this.container.querySelector(`#curtain-customizer-form-${this.sectionId}`);
    
    if (successMessage && form) {
      form.style.display = 'none';
      successMessage.style.display = 'block';
      successMessage.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      
      // Mark review step as completed
      this.isStep4Completed = true;
      this.currentStep = this.totalSteps;
      this.updateStepIndicator();
      this.updateSuccessCushionUI();
    }
  }

  showError(message) {
    const errorMessage = this.container.querySelector(`#error-message-${this.sectionId}`);
    const errorText = this.container.querySelector(`#error-text-${this.sectionId}`);
    
    if (errorMessage && errorText) {
      errorText.textContent = message;
      errorMessage.style.display = 'block';
      errorMessage.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      
      // Hide error after 5 seconds
      setTimeout(() => {
        errorMessage.style.display = 'none';
      }, 5000);
    }
  }

  resetForm() {
    // Reset form data
    this.formData = {
      headingStyle: null,
      width: null,
      drop: null,
      panelType: null,
      fullness: null,
      joinType: null,
      eyeletColor: null,
      roomName: null,
      addScatterCushions: false,
      trackHookCount: null,
      waveHooksMismatchAcknowledged: false
    };
    this.cushionsAddedWithCurtain = false;

    // Reset UI
    this.currentStep = 1;
    this.totalSteps = 4;
    this.isStep4Completed = false;
    this.showStep(1);
    this.updateStepIndicator();

    // Hide step 5 indicator
    const step5Indicator = this.container.querySelector(`#step-indicator-5-${this.sectionId}`);
    if (step5Indicator) step5Indicator.style.display = 'none';
    
    // Reset step labels
    const step2Label = this.container.querySelector(`#step-2-label-${this.sectionId}`);
    const step3Label = this.container.querySelector(`#step-3-label-${this.sectionId}`);
    const step4Label = this.container.querySelector(`#step-4-label-${this.sectionId}`);
    if (step2Label) step2Label.textContent = 'Fullness';
    if (step3Label) step3Label.textContent = 'Heading Style';
    if (step4Label) step4Label.textContent = 'Review';

    // Clear selections
    this.container.querySelectorAll('.heading-style-option').forEach(opt => opt.classList.remove('selected'));
    this.container.querySelectorAll('.panel-type-option').forEach(opt => {
      opt.classList.remove('selected', 'panel-type-option--disabled');
      opt.removeAttribute('aria-disabled');
    });
    this.container.querySelectorAll('.fullness-option').forEach(opt => opt.classList.remove('selected'));
    this.container.querySelectorAll('.eyelet-option').forEach(opt => opt.classList.remove('selected'));
    this.container.querySelectorAll('.join-type-option').forEach(opt => {
      opt.classList.remove('selected', 'join-type-option--disabled');
      opt.removeAttribute('aria-disabled');
    });

    // Ensure fullness options visibility is reset (e.g. show 2X again)
    this.updateFullnessOptionsForHeadingStyle();
    // Restore default fullness selection to mirror initial load behavior
    this.setDefaultFullness();
    this.setDefaultHeadingStyle();

    // Hide join type section and clear hidden input
    const joinSection = this.container.querySelector(`#join-type-section-${this.sectionId}`);
    if (joinSection) joinSection.style.display = 'none';
    const joinTypeInput = this.container.querySelector(`#join-type-${this.sectionId}`);
    if (joinTypeInput) joinTypeInput.value = '';

    const waveHooksSection = this.container.querySelector(`#wave-hooks-section-${this.sectionId}`);
    if (waveHooksSection) waveHooksSection.style.display = 'none';
    this.clearWaveHooks();

    const totalWidthLabelEl = this.container.querySelector(`#summary-total-width-label-${this.sectionId}`);
    if (totalWidthLabelEl) totalWidthLabelEl.textContent = 'Total Width with Fullness:';
    const trackHooksItemEl = this.container.querySelector(`#summary-track-hooks-item-${this.sectionId}`);
    if (trackHooksItemEl) trackHooksItemEl.style.display = 'none';
    const fullnessEl = this.container.querySelector(`#summary-fullness-${this.sectionId}`);
    const fullnessItemEl = fullnessEl ? fullnessEl.closest('.summary-item') : null;
    if (fullnessItemEl) fullnessItemEl.style.display = 'flex';

    // Clear inputs
    const widthInput = this.container.querySelector(`#width-input-${this.sectionId}`);
    const dropInput = this.container.querySelector(`#drop-input-${this.sectionId}`);
    const roomNameInput = this.container.querySelector(`#room-name-input-${this.sectionId}`);
    if (widthInput) widthInput.value = '';
    if (dropInput) dropInput.value = '';
    if (roomNameInput) roomNameInput.value = '';

    const cushionCb = this.container.querySelector(`#cushion-upsell-checkbox-${this.sectionId}`);
    if (cushionCb) cushionCb.checked = false;

    const successCushionBlock = this.container.querySelector(`#success-cushion-block-${this.sectionId}`);
    if (successCushionBlock) successCushionBlock.style.display = '';
    this.showHeadingStepNotice('', '');

    // Show form, hide success message
    const form = this.container.querySelector(`#curtain-customizer-form-${this.sectionId}`);
    const successMessage = this.container.querySelector(`#success-message-${this.sectionId}`);
    if (form) form.style.display = 'block';
    if (successMessage) successMessage.style.display = 'none';

    // Hide error
    const errorMessage = this.container.querySelector(`#error-message-${this.sectionId}`);
    if (errorMessage) errorMessage.style.display = 'none';

    this.updatePrice();
    this.updateReviewMeasurementPreview();
    this.validateDimensionsStep();
    this.validateFullnessStep();
    this.validateHeadingStep();
    this.validateEyeletStep();
    this.validateReviewStep();
  }
}

// Initialize customizer - prevent duplicate instances
(function() {
  // Store instances to prevent duplicates
  window.curtainCustomizers = window.curtainCustomizers || {};
  
  function initializeCustomizers() {
    const customizers = document.querySelectorAll('[data-section-id]');
    customizers.forEach(customizer => {
      const sectionId = customizer.dataset.sectionId;
      // Only initialize curtain customizer sections
      if (sectionId && customizer.classList.contains('curtain-customizer-wrapper') && !window.curtainCustomizers[sectionId]) {
        window.curtainCustomizers[sectionId] = new CurtainCustomizer(sectionId);
      }
    });
  }
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeCustomizers);
  } else {
    // DOM already loaded
    initializeCustomizers();
  }
})();

